import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Button,
  Form,
  ProgressBar,
  Badge,
  Offcanvas,
  Accordion,
  Modal,
} from "react-bootstrap";
import {
  IoArrowBack,
  IoArrowBackOutline,
  IoArrowForwardOutline,
} from "react-icons/io5";
import {
  FaTrashAlt,
  FaImages,
  FaAlignLeft,
  FaPaperPlane,
  FaMicrophone,
  FaQuestionCircle,
  FaEye,
  FaCloudUploadAlt,
  FaClone, // [DUPLICATE ICON]
  FaGripVertical, // ✅ NEW (drag handle)
  FaEdit, // ✅ NEW (jump/edit)
} from "react-icons/fa";
import "./Question.scss";

import type {
  Asset,
  QuestionDraft,
  Slide,
  AirplaneEntity,
  Response,
  ResponseSelect,
  ResponseCanvas,
  ResponseText,
  ResponseNumeric,
  ResponseVoice,
} from "./types";

import ScriptOverlaySvg, { AtcLine } from "./components/ScriptOverlaySvg";
import { useNavigate, useSearchParams } from "react-router-dom";
import HelperService from "../../Services/HelperService";
import WebService from "../../Services/WebService";
import RichTextEditor from "../../components/RichTextEditor/RichTextEditor";
import { toast } from "react-toastify";

/** ------------------------------------------------------------------ */
const PLANE_SIZE_MULTIPLIER = 1.5;
/** ------------------------------------------------------------------ */

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

/* ------------------------------------------------------------------ */
// keep the same type
type UploadDropzoneProps = { onUpload: (url: string | null) => void };

const UploadDropzone: React.FC<UploadDropzoneProps> = ({ onUpload }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const uploadToServer = async (file: File, localUrl: string) => {
    try {
      setUploading(true);
      // call your Laravel upload endpoint
      const form = new FormData();
      form.append("image", file);

      const res: any = await WebService.fileUploadAPI({
        action: "/upload-question-image",
        body: form,
      });

      if (res?.url) {
        // swap blob with server URL (keeps UI consistent)
        onUpload(res.url);
      } else {
        // Server returned no URL. The blob: preview only lives in THIS browser
        // session, so saving it leaves a dead reference that shows blank in the
        // player/simulator. Drop the preview and let the author retry.
        console.warn("Upload returned no URL; clearing preview.");
        onUpload(null);
        toast.error("Image upload failed (no URL returned). Please try again.");
      }
    } catch (e) {
      console.error("Upload failed", e);
      // Don't persist the blob: preview — it can't be saved and would render
      // blank in the player/simulator. Clear it so the author knows to retry.
      onUpload(null);
      toast.error(
        "Image upload failed. Check the file size/connection and try again."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleFile = (file?: File | null) => {
    if (!file) {
      onUpload(null);
      return;
    }
    // 1) set an immediate local preview so your canvas updates now
    const localUrl = URL.createObjectURL(file);
    onUpload(localUrl);

    // 2) upload in background and replace with server URL when ready
    uploadToServer(file, localUrl);
  };

  const onClick = () => inputRef.current?.click();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFile(e.target.files?.[0] || null);
  };

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0] || null);
  };

  const prevent: React.DragEventHandler<HTMLDivElement> = (e) =>
    e.preventDefault();

  return (
    <div
      className="upload-full"
      role="button"
      aria-label="Upload image"
      onClick={onClick}
      onDrop={onDrop}
      onDragOver={prevent}
      onDragEnter={prevent}
      style={{ position: "relative" }}
    >
      <FaCloudUploadAlt className="upload-full__icon" />
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={onChange}
      />

      {/* tiny overlay to indicate uploading (kept minimal) */}
      {uploading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          Uploading…
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
const makeDraft = (is_stage_checker: boolean): QuestionDraft => ({
  schema_version: "1.0.0",
  id: HelperService.generateUUID(),
  title: "Untitled",
  slides: [
    {
      id: HelperService.generateUUID(),
      kind: is_stage_checker ? "question" : "instruction",
      title: "",
      body_html: "",
      media: [],
      layout: "image_top",
      expected_time_sec: 0,
    },
  ],
  assets: [],
  responses: [],
  task_steps: [{ id: HelperService.generateUUID(), title: "", body_html: "" }],
  instructions_player: {
    show_nav_buttons: true,
    show_progress: true,
    require_complete_before_questions: false,
    inline_zoom: true,
    auto_advance: false,
  },
  ui: {
    show_stepper: true,
    show_timer: false,
    locale: "en",
    accessibility_mode: false,
  },
  runtime_params: {
    call_sign: "",
    atis: "",
    altitude: "",
    aircraft_type: "",
    show_info_bar: false,
    per_page_bar: false,
  },
});
const normalizeDraft = (incoming: QuestionDraft): QuestionDraft => {
  return {
    ...incoming,
    runtime_params: {
      call_sign: incoming.runtime_params?.call_sign ?? "",
      atis: incoming.runtime_params?.atis ?? "",
      altitude: incoming.runtime_params?.altitude ?? "",
      aircraft_type: incoming.runtime_params?.aircraft_type ?? "",
      show_info_bar: incoming.runtime_params?.show_info_bar ?? false,
      per_page_bar: incoming.runtime_params?.per_page_bar ?? false,
    },
  };
};

/* Helpers to store script on Slide without touching your schema types */
const getScript = (s: Slide): AtcLine[] =>
  ((s as any).script || []) as AtcLine[];
const setScript = (s: Slide, script: AtcLine[]) => {
  (s as any).script = script;
};

/* Expected-answer text for the RES reveal (mirrors the player's getRevealText) */
const getResponseRevealText = (resp?: Response): string => {
  if (!resp) return "";
  switch (resp.type) {
    case "select": {
      const r = resp as ResponseSelect;
      const firstCorrect = (r.options || []).find((o) => o.is_correct);
      return firstCorrect?.label ?? "";
    }
    case "numeric": {
      const r = resp as ResponseNumeric;
      const rng: any = r.evaluation?.range || {};
      if (typeof rng.exact === "number") return String(rng.exact);
      const { min, max } = rng as { min?: number; max?: number };
      if (typeof min === "number" && typeof max === "number")
        return `${min} – ${max}`;
      if (typeof min === "number") return `≥ ${min}`;
      if (typeof max === "number") return `≤ ${max}`;
      return "";
    }
    case "text": {
      const r = resp as ResponseText;
      const p0: any = (r.evaluation?.patterns || [])[0];
      if (!p0) return "";
      return typeof p0 === "string" ? p0 : (p0.pattern ?? "");
    }
    case "voice": {
      const r = resp as ResponseVoice;
      const sets = r.evaluation?.expected_keywords || [];
      return sets
        .map((s) =>
          (s.any_of || [])
            .map((x) => (x || "").trim())
            .filter(Boolean)
            .join(" / "),
        )
        .filter(Boolean)
        .join(". ");
    }
    case "canvas_action": {
      const r = resp as ResponseCanvas;
      return (r.evaluation?.correct_entity_ids || [])[0] ?? "";
    }
    default:
      return "";
  }
};

/* ------------------------------------------------------------------ */
const AddEditQuestion: React.FC = () => {
  const [draft, setDraft] = useState<QuestionDraft>(makeDraft(false));
  const [idx, setIdx] = useState(0);
  const slide = draft.slides[idx];
  const meta = (draft.runtime_params || {}) as Record<string, string>;
  const [metaOpen, setMetaOpen] = useState(false);
  const hasMeta = meta.call_sign || meta.atis || meta.altitude;

  // Top-bar config source: global (runtime_params) OR per-slide (slide.info_bar),
  // depending on the lesson-level "Different bar content per page" toggle.
  const perPageBar = !!(draft.runtime_params as any)?.per_page_bar;
  const barCfg: any = perPageBar
    ? ((slide as any)?.info_bar || {})
    : draft.runtime_params || {};
  const setBarField = (key: string, val: any) =>
    patch((d) => {
      if (perPageBar) {
        const s = d.slides[idx];
        (s as any).info_bar = { ...((s as any).info_bar || {}), [key]: val };
      } else {
        (d.runtime_params as any)[key] = val;
      }
    });

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const lessonUuid = searchParams.get("uuid") || "";
  const viewVersion = (searchParams.get("version") || "draft") as
    | "published"
    | "draft";
  // Where this question page was opened from: "simulator" (Manage Simulators)
  // or empty/"trainer" (Trainer Manager). Drives where we return after publish.
  const source = searchParams.get("source") || "";
  const [isEditMode, setIsEditMode] = useState(false);

  const [currentVersionUuid, setCurrentVersionUuid] = useState<string | null>(
    null,
  );
  const [stageChecker, setIsStageChecker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageBaseUrl, setImageBaseUrl] = useState<string | null>(null);

  const [previewMode, setPreviewMode] = useState<"author" | "preview">(
    "author",
  );
  const isPreview = previewMode === "preview";
  const canEditAirplanes = slide.kind === "question" && !isPreview;

  const [atcText, setAtcText] = useState<Record<string, string>>({});
  const [atcAutoplay, setAtcAutoplay] = useState<Record<string, boolean>>({});
  const [scriptPreview, setScriptPreview] = useState<Record<string, boolean>>(
    {},
  );
  const [revealMap, setRevealMap] = useState<Record<string, boolean>>({});
  // ATC-text label toggle per slide (mirrors the player's showAtc)
  const [atcShowMap, setAtcShowMap] = useState<Record<string, boolean>>({});
  const GROUPS = Array.from({ length: 10 }, (_, i) => `Group${i + 1}`);
  const [groupFilter, setGroupFilter] = useState<string>("ALL");

  const getGroupId = (s: Slide) => ((s as any).group_id || "") as string;
  const setGroupIdOnCurrentSlide = (gid: string) =>
    updateSlide({ ...(slide as any), group_id: gid } as any);
  // ✅ NEW: Outline Offcanvas (right panel)
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [outlineQuery, setOutlineQuery] = useState("");
  const [dragSlideId, setDragSlideId] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const UNGROUPED = "Ungrouped";

  const groupKeyOf = React.useCallback(
    (s: Slide) => {
      const gid = getGroupId(s);
      return gid && gid.trim().length ? gid : UNGROUPED;
    },
    [UNGROUPED, getGroupId],
  );

  const findLastIndex = <T,>(arr: T[], pred: (v: T) => boolean) => {
    for (let i = arr.length - 1; i >= 0; i--) if (pred(arr[i])) return i;
    return -1;
  };

  const applyDraftWithKeep = (
    nextDraft: QuestionDraft,
    keepSlideId?: string,
  ) => {
    const keepId = keepSlideId ?? draft.slides[idx]?.id;
    setDraft(nextDraft);

    const ni = nextDraft.slides.findIndex((s) => s.id === keepId);
    setIdx(ni >= 0 ? ni : 0);
  };

  const jumpToSlide = (targetIdx: number) => {
    const s = draft.slides[targetIdx];
    if (!s) return;

    // ✅ If stage checker + filtered group: auto-switch filter to the slide’s group so nav stays correct.
    if (stageChecker) {
      const g = groupKeyOf(s);
      setGroupFilter(g === UNGROUPED ? "ALL" : g);
    }

    setIdx(targetIdx);
    setOutlineOpen(false);
  };

  const deleteSlideById = (slideId: string) => {
    const s = draft.slides.find((x) => x.id === slideId);
    if (!s) return;

    const label = (s.title || "").trim() || s.id;
    if (
      !window.confirm(
        `Delete this ${s.kind}?\n\n${label}\n\nThis cannot be undone.`,
      )
    )
      return;

    const keepId = draft.slides[idx]?.id;

    const nextDraft = structuredClone(draft);
    const delIndex = nextDraft.slides.findIndex((x) => x.id === slideId);
    if (delIndex === -1) return;

    // remove anchored response for that slide
    nextDraft.responses = nextDraft.responses.filter(
      (r) => r.anchored_to_slide !== slideId,
    );

    nextDraft.slides.splice(delIndex, 1);

    // if last slide removed, ensure at least 1 slide exists
    if (nextDraft.slides.length === 0) {
      nextDraft.slides.push({
        id: HelperService.generateUUID(),
        kind: stageChecker ? "question" : "instruction",
        title: "",
        body_html: "",
        media: [],
        layout: "image_top",
        expected_time_sec: 0,
      } as any);
    }

    // keep current slide if possible, otherwise clamp
    const keepAfter =
      keepId === slideId
        ? nextDraft.slides[Math.max(0, delIndex - 1)]?.id
        : keepId;
    applyDraftWithKeep(nextDraft, keepAfter);
  };

  const moveQuestionTo = (
    fromSlideId: string,
    targetSlideId: string | null,
    targetGroupKey: string,
    place: "before" | "after" | "end" = "before",
  ) => {
    if (!fromSlideId) return;
    const keepId = draft.slides[idx]?.id;

    const nextDraft = structuredClone(draft);
    const slides = nextDraft.slides;

    const fromIndex = slides.findIndex((s) => s.id === fromSlideId);
    if (fromIndex === -1) return;

    const moved = slides.splice(fromIndex, 1)[0];

    // Only questions are shown/dragged; still guard:
    if (stageChecker && moved?.kind === "question") {
      (moved as any).group_id =
        targetGroupKey === UNGROUPED ? "" : targetGroupKey;
    }

    // compute insert index
    let insertIndex = slides.length;

    if (place === "end") {
      // insert after last question of that group
      const last = findLastIndex(
        slides,
        (s) => s.kind === "question" && groupKeyOf(s as any) === targetGroupKey,
      );
      insertIndex = last === -1 ? slides.length : last + 1;
    } else if (targetSlideId) {
      const tIndex = slides.findIndex((s) => s.id === targetSlideId);
      if (tIndex === -1) insertIndex = slides.length;
      else insertIndex = place === "before" ? tIndex : tIndex + 1;
    }

    if (insertIndex < 0) insertIndex = 0;
    if (insertIndex > slides.length) insertIndex = slides.length;

    slides.splice(insertIndex, 0, moved);

    applyDraftWithKeep(nextDraft, keepId);
  };

  const onDragStartSlide = (slideId: string) => setDragSlideId(slideId);
  const onDragEndSlide = () => setDragSlideId(null);

  const visibleIdxs = useMemo(() => {
    // ✅ normal lesson => behave exactly as today (no filtering)
    if (!stageChecker || groupFilter === "ALL") {
      return draft.slides.map((_, i) => i);
    }

    // ✅ stage checker + group selected => show only question slides of that group
    return draft.slides
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => s.kind === "question" && getGroupId(s) === groupFilter)
      .map(({ i }) => i);
  }, [draft.slides, stageChecker, groupFilter]);

  const visiblePos = useMemo(() => {
    const p = visibleIdxs.indexOf(idx);
    return p === -1 ? 0 : p;
  }, [visibleIdxs, idx]);

  // ✅ NEW: Flat list of QUESTIONS (grouping only when stageChecker)
  const outlineQuestions = useMemo(() => {
    const qText = outlineQuery.trim().toLowerCase();

    return (draft.slides || [])
      .map((s, i) => ({ s, i }))
      // Client: the ordering view must also list instruction slides so they can
      // be reordered — not only questions.
      .filter(({ s }) => s.kind === "question" || s.kind === "instruction")
      .filter(({ s }) => {
        if (!qText) return true;
        const t = (s.title || "").toLowerCase();
        const id = (s.id || "").toLowerCase();
        return t.includes(qText) || id.includes(qText);
      });
  }, [draft.slides, outlineQuery]);

  const outlineGroups = useMemo(() => {
    if (!stageChecker) return null;

    const map: Record<string, Array<{ i: number; s: Slide }>> = {};
    for (const it of outlineQuestions) {
      const gk = groupKeyOf(it.s);
      if (!map[gk]) map[gk] = [];
      map[gk].push(it);
    }

    const order = [...GROUPS, UNGROUPED]; // fixed order for stage checker
    return { map, order };
  }, [stageChecker, outlineQuestions, groupKeyOf, GROUPS]);

  const patch = (fn: (d: QuestionDraft) => void) =>
    setDraft((cur) => {
      const next = structuredClone(cur);
      fn(next);
      setIsDirty(true);
      return next;
    });

  const updateSlide = (patchSlide: Partial<Slide>) =>
    patch((d) => Object.assign(d.slides[idx], patchSlide));

  /** set or clear image in media[index] (keeps the other slot untouched) */
  const setMediaAt = (index: 0 | 1, url: string | null) => {
    updateSlide({
      media: (() => {
        const cur = (slide.media || []).slice(0, 2);
        // ensure length 2 so we can address media[1]
        if (cur.length < 2) cur.length = 2;
        cur[index] = url || (undefined as any);
        // trim trailing empties
        const trimmed = [...cur];
        while (trimmed.length && !trimmed[trimmed.length - 1]) trimmed.pop();
        return trimmed.filter(Boolean) as string[];
      })(),
    });
  };

  const removeMediaAt = (index: 0 | 1) => setMediaAt(index, null);
  const slideResponse: Response | undefined = useMemo(
    () => draft.responses.find((r) => r.anchored_to_slide === slide.id),
    [draft.responses, slide.id],
  );

  const isCanvasAction =
    slide.kind === "question" && slideResponse?.type === "canvas_action";

  // Label text sources for the ATC / RES footer buttons (player-style)
  const atcLabelText =
    (slide as any).atc?.text?.trim() ||
    atcText[slide.id]?.trim() ||
    getScript(slide)
      .map((l) => l.text)
      .filter(Boolean)
      .join(" ");
  const responseLabelText = getResponseRevealText(slideResponse);
  const atcShown = !!atcShowMap[slide.id];
  const resShown = !!revealMap[slide.id];

  const setResponseType = (t: Response["type"]) => {
    const existing = slideResponse;
    if (!existing) {
      const id = HelperService.generateUUID(); // UUID instead of R1/R2...
      const base = {
        id,
        anchored_to_slide: slide.id,
        links_to_steps: [],
        prompt: "",
      };
      let r: Response;
      if (t === "select") {
        r = {
          ...base,
          type: "select",
          options: [
            {
              id: HelperService.generateUUID(),
              label: "Option 1",
              is_correct: true,
            },
            {
              id: HelperService.generateUUID(),
              label: "Option 2",
              is_correct: false,
            },
          ],
        } as ResponseSelect;
      } else if (t === "text") {
        r = {
          ...base,
          type: "text",
          evaluation: {
            strategy: "regex",
            patterns: [],
            flags: { case_insensitive: true },
            scoring: { points: 1 },
          },
        } as ResponseText;
      } else if (t === "numeric") {
        r = {
          ...base,
          type: "numeric",
          evaluation: {
            strategy: "numeric_range",
            range: {},
            scoring: { points: 1 },
          },
        } as ResponseNumeric;
      } else if (t === "voice") {
        r = {
          ...base,
          type: "voice",
          voice: {
            language: "en-IN",
            recording_limit_sec: 15,
            silence_timeout_sec: 2,
          },
          evaluation: {
            strategy: "voice_asr",
            expected_keywords: [{ any_of: ["cleared to land"] }],
            min_confidence: 0.6,
            scoring: { points: 1 },
          },
        } as ResponseVoice;
      } else {
        r = {
          ...base,
          type: "canvas_action",
          evaluation: {
            strategy: "hotspot_select",
            correct_entity_ids: [],
            scoring: { points: 1 },
          },
        } as ResponseCanvas;
      }
      patch((d) => {
        d.responses.push(r);
      });
    } else if (existing.type !== t) {
      patch((d) => {
        d.responses = d.responses.map((r) => {
          if (r.id !== existing.id) return r;
          const base = {
            id: r.id,
            anchored_to_slide: r.anchored_to_slide,
            links_to_steps: r.links_to_steps,
            prompt: r.prompt,
          };
          if (t === "select")
            return { ...base, type: "select", options: [] } as ResponseSelect;
          if (t === "text")
            return {
              ...base,
              type: "text",
              evaluation: {
                strategy: "regex",
                patterns: [],
                flags: { case_insensitive: true },
                scoring: { points: 1 },
              },
            } as ResponseText;
          if (t === "numeric")
            return {
              ...base,
              type: "numeric",
              evaluation: {
                strategy: "numeric_range",
                range: {},
                scoring: { points: 1 },
              },
            } as ResponseNumeric;
          if (t === "voice")
            return {
              ...base,
              type: "voice",
              voice: {
                language: "en-IN",
                recording_limit_sec: 15,
                silence_timeout_sec: 2,
              },
              evaluation: {
                strategy: "voice_asr",
                expected_keywords: [],
                min_confidence: 0.6,
                scoring: { points: 1 },
              },
            } as ResponseVoice;
          return {
            ...base,
            type: "canvas_action",
            evaluation: {
              strategy: "hotspot_select",
              correct_entity_ids: [],
              scoring: { points: 1 },
            },
          } as ResponseCanvas;
        });
      });
    }
  };

  const mutateResponse = (mut: (r: Response) => Response) => {
    if (!slideResponse) return;
    patch((d) => {
      d.responses = d.responses.map((r) =>
        r.id === slideResponse.id ? mut(r) : r,
      );
    });
  };

  const normAngle = (deg: number) => ((deg % 360) + 360) % 360;

  const displayType: "image" | "double" | "text" = useMemo(() => {
    if (slide.layout === "image_left") return "double";
    if (slide.layout === "image_top") return "image";
    return "text";
  }, [slide.layout]);

  /* ---------------- Navigation ---------------- */
  const playNextSfx = () => {
    try {
      const ctx = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 600;
      g.gain.value = 0.001;
      osc.connect(g).connect(ctx.destination);
      osc.start();
      const t = ctx.currentTime;
      g.gain.exponentialRampToValueAtTime(0.07, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
      osc.stop(t + 0.14);
    } catch {}
  };
  const prev = () => {
    // ✅ normal lesson => same logic as today
    if (!stageChecker || groupFilter === "ALL") {
      setIdx((i) => Math.max(0, i - 1));
      return;
    }

    // ✅ stage checker + group selected
    if (!visibleIdxs.length) return;
    const p = visibleIdxs.indexOf(idx);
    const nextPos = Math.max(0, (p === -1 ? 0 : p) - 1);
    setIdx(visibleIdxs[nextPos]);
  };

  const next = () => {
    // ✅ normal lesson => same logic as today
    if (!stageChecker || groupFilter === "ALL") {
      if (isPreview) playNextSfx();
      setIdx((i) => Math.min(draft.slides.length - 1, i + 1));
      return;
    }

    // ✅ stage checker + group selected
    if (!visibleIdxs.length) return;
    if (isPreview) playNextSfx();
    const p = visibleIdxs.indexOf(idx);
    const nextPos = Math.min(visibleIdxs.length - 1, (p === -1 ? 0 : p) + 1);
    setIdx(visibleIdxs[nextPos]);
  };

  /* ---------------- Airplanes ---------------- */
  const [activeAp, setActiveAp] = useState<string | null>(null);
  const addAirplane = () => {
    if (slide.kind !== "question") return;
    const list = slide.airplanes ?? [];
    const id = `ap${list.length + 1}`;
    const ap: AirplaneEntity = {
      id,
      x: 50,
      y: 50,
      heading_deg: 0,
      color: "#0b3fad",
      snap: { enabled: true, step_deg: 5 },
      compass: { show: true, bezel: false },
    };
    updateSlide({ airplanes: [...list, ap] });
    setActiveAp(id);
  };
  // const removeAirplane = (id: string) => {
  //   if (slide.kind !== "question") return;
  //   updateSlide({
  //     airplanes: (slide.airplanes || []).filter((a) => a.id !== id),
  //   });
  //   if (activeAp === id) setActiveAp(null);
  // };
  const removeAllAirplanes = () => {
    if (slide.kind !== "question") return;

    if (!slide.airplanes || slide.airplanes.length === 0) return;

    if (!window.confirm("Remove all airplanes from this slide?")) return;

    updateSlide({ airplanes: [] });
    setActiveAp(null);
  };

  /* ---------------- Preview selection ---------------- */
  const [planeSelect, setPlaneSelect] = useState<Record<string, string[]>>({});
  const [canvasFeedback, setCanvasFeedback] = useState<
    Record<string, "" | "correct" | "wrong">
  >({});
  const planeSelected = planeSelect[slide.id] || [];
  const [submittedMap, setSubmittedMap] = useState<Record<string, boolean>>({});

  const evaluatePlaneSelection = (chosenIds: string[]) => {
    const r = slideResponse as ResponseCanvas | undefined;
    if (!r || r.type !== "canvas_action") return "";
    const ev = r.evaluation;
    if (ev?.strategy !== "hotspot_select") return "";
    const expected = new Set(ev.correct_entity_ids || []);
    const chosen = new Set(chosenIds);
    const mismatch =
      Array.from(expected).some((id) => !chosen.has(id)) ||
      Array.from(chosen).some((id) => !expected.has(id));
    if (expected.size === 0) return "";
    return mismatch ? "wrong" : "correct";
  };

  const togglePlaneSelect = (planeId: string) => {
    if (!isPreview) return;
    setRevealMap((m) => ({ ...m, [slide.id]: false }));
    setSubmittedMap((m) => ({ ...m, [slide.id]: false }));
    setPlaneSelect((prev) => {
      const current = prev[slide.id] || [];
      const already = current[0] === planeId;
      const next = already ? [] : [planeId]; // single-select
      const fb = evaluatePlaneSelection(next);
      setCanvasFeedback((f) => ({ ...f, [slide.id]: fb }));
      return { ...prev, [slide.id]: next };
    });
  };

  /* ---------------- SVG metrics ---------------- */
  const [vb, setVb] = useState<{ w: number; h: number }>({ w: 1400, h: 1820 });
  const svgRef = useRef<SVGSVGElement | null>(null);

  const updateMetricsRef = useRef<() => void>(() => {});
  const requestMeasureSoon = () => {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => updateMetricsRef.current?.()),
    );
  };
  const useImageLoader = (src?: string) => {
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
      if (!src) {
        setLoaded(false);
        return;
      }

      setLoaded(false);
      const img = new Image();

      img.onload = () => setLoaded(true);
      img.onerror = () => setLoaded(false);

      img.src = src;
    }, [src]);

    return loaded;
  };
  const media0 = slide.media?.[0];
  const imageSrc = media0
    ? imageBaseUrl
      ? `${imageBaseUrl}/${media0}`
      : media0
    : undefined;

  const imageLoaded = useImageLoader(imageSrc);

  // LEFT image (media[0])
  const leftSrc = slide.media?.[0]
    ? imageBaseUrl
      ? `${imageBaseUrl}/${slide.media[0]}`
      : slide.media[0]
    : undefined;

  const leftLoaded = useImageLoader(leftSrc);

  // RIGHT image (media[1])
  const rightSrc = slide.media?.[1]
    ? imageBaseUrl
      ? `${imageBaseUrl}/${slide.media[1]}`
      : slide.media[1]
    : undefined;

  const rightLoaded = useImageLoader(rightSrc);

  const AtcDimLayer: React.FC<{ opacity?: number }> = ({ opacity = 0 }) => {
    if (opacity <= 0) return null;

    // Perceptual scaling (gamma curve)
    const perceptual = Math.pow(opacity, 1.6);

    const overlayAlpha = perceptual * 0.45; // cap whitening
    const blurPx = perceptual * 6; // gentle blur

    return (
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 5,
          background: `rgba(255,255,255,${overlayAlpha})`,
          backdropFilter: `blur(${blurPx}px)`,
          WebkitBackdropFilter: `blur(${blurPx}px)`,
          pointerEvents: "none",
          transition: "all 0.25s ease",
        }}
      />
    );
  };

  useEffect(() => {
    if (!media0) return;
    const img = new Image();
    img.onload = () => {
      setVb({ w: img.naturalWidth || 1400, h: img.naturalHeight || 1820 });
      requestMeasureSoon();
    };
    img.src = imageBaseUrl ? imageBaseUrl + "/" + media0 : media0;
  }, [media0, imageBaseUrl]);

  const pctToVb = (xPct: number, yPct: number) => ({
    x: (xPct / 100) * vb.w,
    y: (yPct / 100) * vb.h,
  });
  const clampVb = (x: number, y: number, pad = 20) => ({
    x: Math.max(pad, Math.min(vb.w - pad, x)),
    y: Math.max(pad, Math.min(vb.h - pad, y)),
  });

  const clientToVb = (clientX: number, clientY: number) => {
    const el = svgRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * vb.w,
      y: ((clientY - rect.top) / rect.height) * vb.h,
    };
  };

  const [svgMetrics, setSvgMetrics] = useState({
    scale: 1,
    width: 0,
    height: 0,
    planePx: 40,
  });

  const updateMetrics = () => {
    const el = svgRef.current!;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width < 20 || rect.height < 20) {
      requestMeasureSoon();
      return;
    }
    const s = Math.min(rect.width / vb.w, rect.height / vb.h) || 1;
    const basePct = 0.035 * PLANE_SIZE_MULTIPLIER;
    const px = Math.round(
      clamp(Math.min(rect.width, rect.height) * basePct, 32, 96),
    );
    setSvgMetrics({
      scale: s,
      width: rect.width,
      height: rect.height,
      planePx: px,
    });
  };
  updateMetricsRef.current = updateMetrics;

  useEffect(() => {
    if (!svgRef.current) return;
    const el = svgRef.current;
    requestMeasureSoon();
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(updateMetrics);
    });
    ro.observe(el);
    window.addEventListener("resize", updateMetrics);
    const onWinLoad = () => requestAnimationFrame(updateMetrics);
    window.addEventListener("load", onWinLoad);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateMetrics);
      window.removeEventListener("load", onWinLoad);
    };
  }, [vb.w, vb.h, isPreview, idx, displayType]);

  /* ---------------- Drag / Rotate (rAF smoothed) ---------------- */
  type DragState = null | {
    id: string;
    mode: "move" | "rotate";
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    cx: number;
    cy: number;
  };
  const [drag, setDrag] = useState<DragState>(null);
  const dragRef = useRef<DragState>(null);
  useEffect(() => {
    dragRef.current = drag;
  }, [drag]);
  const rafRef = useRef<number | null>(null);
  const lastMoveRef = useRef<{ x: number; y: number } | null>(null);

  const scheduleDragUpdate = () => {
    if (rafRef.current != null) return;

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;

      const ev = lastMoveRef.current;
      const st = dragRef.current as
        | (DragState & {
            startHeading?: number; // from startDrag
            ang0?: number; // from startDrag
          })
        | null;

      if (!ev || !st || !canEditAirplanes) return;

      const { x: mx, y: my } = clientToVb(ev.x, ev.y);

      if (st.mode === "move") {
        // (unchanged)
        const nx = st.origX + (mx - st.startX);
        const ny = st.origY + (my - st.startY);
        const c = clampVb(nx, ny, 20);
        const xPct = (c.x / vb.w) * 100;
        const yPct = (c.y / vb.h) * 100;

        patch((d) => {
          const s = d.slides[idx];
          s.airplanes = (s.airplanes || []).map((a) =>
            a.id === st.id ? { ...a, x: xPct, y: yPct } : a,
          );
        });
        return;
      }

      // ---- ROTATE (anchored to startHeading + initial mouse angle) ----
      // Guard: if for some reason we missed init, fall back to current plane heading
      let startHeading = st.startHeading;
      let ang0 = st.ang0;
      if (startHeading === undefined || ang0 === undefined) {
        const ap = (slide.airplanes || []).find((a) => a.id === st.id);
        startHeading = ap?.heading_deg ?? 0;
        ang0 = Math.atan2(my - st.cy, mx - st.cx) * (180 / Math.PI);
        st.startHeading = startHeading;
        st.ang0 = ang0;
      }

      const curAng = Math.atan2(my - st.cy, mx - st.cx) * (180 / Math.PI);
      let delta = curAng - ang0;
      // unwrap seam
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;

      let nextHeading = startHeading + delta;

      // keep your snap logic
      const ap = (slide.airplanes || []).find((a) => a.id === st.id);
      const step = ap?.snap?.enabled ? (ap?.snap?.step_deg ?? 5) : 0;
      if (step && step > 0) {
        nextHeading = Math.round(nextHeading / step) * step;
      }

      // normalize to [0,360)
      nextHeading = ((nextHeading % 360) + 360) % 360;

      patch((d) => {
        const s = d.slides[idx];
        s.airplanes = (s.airplanes || []).map((a) =>
          a.id === st.id ? { ...a, heading_deg: nextHeading } : a,
        );
      });
    });
  };

  const startDrag = (apId: string, e: React.MouseEvent, rotate: boolean) => {
    if (!canEditAirplanes) return;
    e.preventDefault();
    e.stopPropagation();
    setActiveAp(apId);

    const ap = (slide.airplanes || []).find((a) => a.id === apId);
    if (!ap) return;

    const { x, y } = pctToVb(ap.x, ap.y);
    const { x: mx, y: my } = clientToVb(e.clientX, e.clientY);

    const st: DragState & {
      startHeading?: number; // NEW
      ang0?: number; // NEW
    } = {
      id: apId,
      mode: rotate ? "rotate" : "move",
      startX: mx,
      startY: my,
      origX: x,
      origY: y,
      cx: x,
      cy: y,
    };

    // NEW: anchor rotation to current heading + initial mouse angle
    if (rotate) {
      st.startHeading = ap.heading_deg ?? 0;
      st.ang0 = Math.atan2(my - y, mx - x) * (180 / Math.PI);
    }

    setDrag(st);
    dragRef.current = st;
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!dragRef.current || !canEditAirplanes) return;
    lastMoveRef.current = { x: e.clientX, y: e.clientY };
    scheduleDragUpdate();
  };
  const endDrag = () => {
    const st = dragRef.current; // capture before clearing
    setDrag(null);
    dragRef.current = null;
    lastMoveRef.current = null;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    // Snap once at the end of rotation (keeps drag smooth)
    if (st?.mode === "rotate") {
      patch((d) => {
        const s = d.slides[idx];
        s.airplanes = (s.airplanes || []).map((a) => {
          if (a.id !== st.id) return a;
          const step = a.snap?.enabled ? (a.snap?.step_deg ?? 5) : 0;
          if (!step) return a;
          const snapped = Math.round((a.heading_deg || 0) / step) * step;
          return { ...a, heading_deg: normAngle(snapped) };
        });
      });
    }
  };
  useEffect(() => {
    const mm = (e: MouseEvent) => onMouseMove(e);
    const mu = () => endDrag();
    window.addEventListener("mousemove", mm, { passive: true });
    window.addEventListener("mouseup", mu, { passive: true });
    return () => {
      window.removeEventListener("mousemove", mm as any);
      window.removeEventListener("mouseup", mu as any);
    };
  }, [canEditAirplanes, vb.w, vb.h, idx]);

  const rotateDelta = (apId: string, deltaDeg: number) => {
    patch((d) => {
      const s = d.slides[idx];
      s.airplanes = (s.airplanes || []).map((a) =>
        a.id === apId
          ? {
              ...a,
              heading_deg:
                ((((a.heading_deg || 0) + deltaDeg) % 360) + 360) % 360,
            }
          : a,
      );
    });
  };

  const moveDelta = (apId: string, dxPx: number, dyPx: number) => {
    const ap = (slide.airplanes || []).find((a) => a.id === apId);
    if (!ap || !svgRef.current) return;
    const cur = pctToVb(ap.x, ap.y);
    const rect = svgRef.current.getBoundingClientRect();
    const dxVb = (dxPx / rect.width) * vb.w;
    const dyVb = (dyPx / rect.height) * vb.h;
    const c = clampVb(cur.x + dxVb, cur.y + dyVb, 20);
    const xPct = (c.x / vb.w) * 100;
    const yPct = (c.y / vb.h) * 100;
    patch((d) => {
      const s = d.slides[idx];
      s.airplanes = (s.airplanes || []).map((a) =>
        a.id === apId ? { ...a, x: xPct, y: yPct } : a,
      );
    });
  };

  /* ---------------- Voice + ATC TTS ---------------- */
  type VoicePreview = { recording: boolean; url?: string; asrText: string };
  const [voiceMap, setVoiceMap] = useState<Record<string, VoicePreview>>({});
  const mediaRefs = useRef<Record<string, MediaRecorder | null>>({});

  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        setVoiceMap((prev) => ({
          ...prev,
          [slide.id]: {
            ...(prev[slide.id] || { asrText: "" }),
            recording: false,
            url,
          },
        }));
      };
      mediaRefs.current[slide.id] = rec;
      setVoiceMap((prev) => ({
        ...prev,
        [slide.id]: { ...(prev[slide.id] || { asrText: "" }), recording: true },
      }));
      rec.start();
    } catch {
      alert("Microphone not available.");
    }
  };
  const stopRecord = () => {
    const rec = mediaRefs.current[slide.id];
    if (rec && rec.state !== "inactive") rec.stop();
    mediaRefs.current[slide.id] = null;
  };

  const radioSpeak = (text: string) => {
    const txt = (text || "").trim();
    if (!txt) return;
    try {
      const u = new SpeechSynthesisUtterance(txt);
      u.rate = 1.0;
      u.pitch = 0.9;
      window.speechSynthesis.speak(u);
    } catch {}
  };
  const playATC = (overrideText?: string) => {
    const txt = overrideText ?? atcText[slide.id];
    radioSpeak(txt || "");
  };

  // Speak an array of lines sequentially (no per-line cancel)
  const speakSequence = (lines: string[], rate = 1.0, pitch = 0.9) => {
    const synth = window.speechSynthesis;
    try {
      synth.cancel();
    } catch {}
    const textList = lines.map((t) => (t || "").trim()).filter(Boolean);
    if (!textList.length) return;

    let i = 0;
    const speakNext = () => {
      if (i >= textList.length) return;
      const u = new SpeechSynthesisUtterance(textList[i]);
      u.rate = rate;
      u.pitch = pitch;
      u.onend = () => {
        i += 1;
        speakNext();
      };
      synth.speak(u);
    };
    speakNext();
  };

  useEffect(() => {
    if (!isPreview) return;
    const auto = getAtc(slide).autoplay;
    if (!auto) return;
    const script = getScript(slide);

    if (script.length) {
      const pick = script.some((l) => l.tts)
        ? script.filter((l) => l.tts).map((l) => l.text)
        : script.map((l) => l.text);
      speakSequence(pick, 1.0, 0.9);
    } else {
      playATC(); // legacy single text
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreview, idx, atcAutoplay[slide.id]]);

  /* ---------------- Check / Submit ---------------- */
  const banner = (ok: boolean, text?: string) => {
    if (isPreview) return;
    const el = document.getElementById("check-banner");
    if (el) {
      el.innerText = text ?? (ok ? "✅ Correct" : "❌ Not quite");
      el.className = `check-banner ${ok ? "ok" : "bad"}`;
      window.setTimeout(() => {
        const el2 = document.getElementById("check-banner");
        if (el2) el2.className = "check-banner";
      }, 1500);
    }
  };

  const checkAnswer = () => {
    const r = slideResponse;
    if (!r) return;
    let ok = false;
    if (r.type === "canvas_action") {
      const ev = r.evaluation;
      if (ev?.strategy === "hotspot_select") {
        const expected = new Set(ev.correct_entity_ids || []);
        const chosen = new Set(planeSelected);
        const mismatch =
          Array.from(expected).some((id) => !chosen.has(id)) ||
          Array.from(chosen).some((id) => !expected.has(id));
        ok = !mismatch && expected.size > 0;
        setCanvasFeedback((f) => ({
          ...f,
          [slide.id]: ok ? "correct" : "wrong",
        }));
      }
    }
    banner(ok);
  };

  const handleSubmitCanvas = () => {
    if (!(isPreview && isCanvasAction)) return;
    checkAnswer();
    setSubmittedMap((m) => ({ ...m, [slide.id]: true }));
  };

  const toggleReveal = () => {
    const next = !revealMap[slide.id];
    setRevealMap((m) => ({ ...m, [slide.id]: next }));
  };

  useEffect(() => {
    if (!isPreview) {
      setPlaneSelect((prev) => ({ ...prev, [slide.id]: [] }));
      setSubmittedMap((m) => ({ ...m, [slide.id]: false }));
    }
  }, [isPreview, slide.id]);

  /* ---------------- SVG plane glyph ---------------- */
  const PlaneGlyph: React.FC<{ fill?: string }> = ({ fill = "#0D6EFD" }) => (
    <path
      d="M21.1268 47.9118C22.1569 47.8755 23.0841 47.2162 23.5343 46.2031L30.5693 28.0206C40.8864 27.9991 48 26.7531 48 24C48 21.247 40.8864 20.001 30.5693 19.9796L23.5344 1.79689C23.0841 0.782324 22.1569 0.124669 21.1268 0.0882001L18.6858 0.000731374C18.3168 -0.0125811 17.962 0.156075 17.7185 0.461699C17.475 0.767418 17.3687 1.17392 17.429 1.57217C17.429 1.57217 19.5519 15.792 20.2355 20.1381C12.5 21.5 8.1178 22.5149 8.1178 22.5149L5.82331 15.7044C5.59733 15.1376 5.04417 14.7658 4.42749 14.7658L3.52881 14.7658C3.11617 14.7658 2.72129 14.9327 2.43758 15.2302C2.15398 15.526 2.00662 15.9242 2.03007 16.3308L2.37824 22.4086C1.00758 22.6036 0 23.2415 0 24C0 24.7586 1.00758 25.3948 2.37824 25.5914L2.03007 31.6694C2.00671 32.0759 2.15398 32.4742 2.43758 32.7699C2.72128 33.0674 3.11627 33.2343 3.52881 33.2343L4.42748 33.2343C5.04417 33.2343 5.59733 32.8625 5.82331 32.2957L8.1178 25.4836C8.1178 25.4836 12.5 26.5 20.2355 27.8621C19.5519 32.2081 17.429 46.4279 17.429 46.4279C17.3687 46.8262 17.475 47.2344 17.7185 47.5384C17.962 47.8441 18.3168 48.011 18.6858 47.9994L21.1268 47.9118Z"
      fill={fill}
    />
  );

  /* ---------------- SVG canvas ---------------- */
  const renderSvgCanvas = () => {
    if (displayType !== "image") return null;
    const planes = slide.airplanes || [];
    const r = slideResponse as ResponseCanvas | undefined;
    const hotspotMode =
      isPreview &&
      r?.type === "canvas_action" &&
      (r.evaluation?.strategy ?? "hotspot_select") === "hotspot_select";
    const expectedIds: string[] =
      r?.type === "canvas_action" && r.evaluation?.strategy === "hotspot_select"
        ? r.evaluation?.correct_entity_ids || []
        : [];

    const planeSizeVb = svgMetrics.planePx / (svgMetrics.scale || 1);
    const planeScale = planeSizeVb / 48;
    const px = (v: number) => v / (svgMetrics.scale || 1);
    const half = planeSizeVb / 2;

    const submitted = !!submittedMap[slide.id];

    return (
      <svg
        ref={svgRef}
        viewBox={`0 0 ${vb.w} ${vb.h}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-100 h-100"
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          width: "100%",
          height: "auto",
          overflow: "visible",
          display: "block",
        }}
      >
        <defs>
          <filter
            id="blur5"
            filterUnits="userSpaceOnUse"
            x={-vb.w * 2}
            y={-vb.h * 2}
            width={vb.w * 5}
            height={vb.h * 5}
          >
            <feGaussianBlur stdDeviation="6" />
          </filter>
        </defs>

        {/* Background image */}
        {slide.media?.[0] &&
          (submitted ? (
            <g
              className="svgItem"
              filter="url(#blur5)"
              style={{ opacity: 0.6 }}
            >
              <image
                href={imageBaseUrl + "/" + slide.media[0]}
                x="0"
                y="0"
                width={vb.w}
                height={vb.h}
                preserveAspectRatio="xMidYMid meet"
              />
            </g>
          ) : (
            <image
              href={imageBaseUrl + "/" + slide.media[0]}
              x="0"
              y="0"
              width={vb.w}
              height={vb.h}
              preserveAspectRatio="xMidYMid meet"
            />
          ))}

        {/* Planes */}
        {planes.map((ap) => {
          const pos = pctToVb(ap.x, ap.y);
          const selected = (planeSelected || []).includes(ap.id);
          const isCorrectAns = expectedIds.includes(ap.id);

          let fillColor = ap.color || "#0D6EFD";
          if (submitted && hotspotMode) {
            if (isCorrectAns) fillColor = "#16a34a";
            else if (selected) fillColor = "#dc2626";
          }
          const blurred =
            submitted && hotspotMode && !(selected || isCorrectAns);

          const handleMouseDown = (e: React.MouseEvent) => {
            if (isPreview) {
              setPlaneSelect((prev) => ({ ...prev, [slide.id]: [ap.id] }));
              return;
            }
            const rotate = e.shiftKey || e.button === 2;
            startDrag(ap.id, e, rotate);
          };
          const onContext = (e: React.MouseEvent) => {
            if (!canEditAirplanes) return;
            e.preventDefault();
            startDrag(ap.id, e, true);
          };
          const onWheel: React.WheelEventHandler = (e) => {
            if (!canEditAirplanes) return;
            e.preventDefault();
            rotateDelta(ap.id, e.deltaY > 0 ? 5 : -5);
          };

          const baseOpacity =
            submitted && hotspotMode
              ? selected || isCorrectAns
                ? 1
                : 0.6
              : selected
                ? 1
                : 0.9;

          return (
            <g
              key={ap.id}
              transform={`translate(${pos.x}, ${pos.y}) rotate(${
                ap.heading_deg || 0
              })`}
              style={{
                cursor: canEditAirplanes ? "grab" : "pointer",
                filter: blurred
                  ? "url(#blur5)"
                  : selected
                    ? "drop-shadow(0 2px 3px rgba(59,130,246,0.45))"
                    : "none",
                opacity: baseOpacity,
              }}
              onMouseDown={handleMouseDown}
              onContextMenu={onContext}
              onWheel={onWheel}
            >
              {!submitted && selected && (
                <circle
                  cx={0}
                  cy={0}
                  r={half + px(8)}
                  fill="transparent"
                  stroke="#2563eb"
                  strokeWidth={px(3)}
                />
              )}
              {canEditAirplanes && (
                <circle
                  cx={0}
                  cy={-(half + px(14))}
                  r={px(6)}
                  fill="#fff"
                  stroke="#0b3fad"
                  strokeWidth={px(2)}
                  onMouseDown={(e) => startDrag(ap.id, e, true)}
                />
              )}
              <g
                transform={`translate(${-24 * planeScale}, ${
                  -24 * planeScale
                }) scale(${planeScale})`}
              >
                <path
                  d="M21.1268 47.9118C22.1569 47.8755 23.0841 47.2162 23.5343 46.2031L30.5693 28.0206C40.8864 27.9991 48 26.7531 48 24C48 21.247 40.8864 20.001 30.5693 19.9796L23.5344 1.79689C23.0841 0.782324 22.1569 0.124669 21.1268 0.0882001L18.6858 0.000731374C18.3168 -0.0125811 17.962 0.156075 17.7185 0.461699C17.475 0.767418 17.3687 1.17392 17.429 1.57217C17.429 1.57217 19.5519 15.792 20.2355 20.1381C12.5 21.5 8.1178 22.5149 8.1178 22.5149L5.82331 15.7044C5.59733 15.1376 5.04417 14.7658 4.42749 14.7658L3.52881 14.7658C3.11617 14.7658 2.72129 14.9327 2.43758 15.2302C2.15398 15.526 2.00662 15.9242 2.03007 16.3308L2.37824 22.4086C1.00758 22.6036 0 23.2415 0 24C0 24.7586 1.00758 25.3948 2.37824 25.5914L2.03007 31.6694C2.00671 32.0759 2.15398 32.4742 2.43758 32.7699C2.72128 33.0674 3.11627 33.2343 3.52881 33.2343L4.42748 33.2343C5.04417 33.2343 5.59733 32.8625 5.82331 32.2957L8.1178 25.4836C8.1178 25.4836 12.5 26.5 20.2355 27.8621C19.5519 32.2081 17.429 46.4279 17.429 46.4279C17.3687 46.8262 17.475 47.2344 17.7185 47.5384C17.962 47.8441 18.3168 48.011 18.6858 47.9994L21.1268 47.9118Z"
                  fill={fillColor}
                />
              </g>
              {!isPreview && (
                <text
                  x={0}
                  y={-(half + px(24))}
                  textAnchor="middle"
                  fontSize={px(11)}
                  fontWeight={700}
                  fill="#0f172a"
                >
                  {ap.id}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    );
  };

  /** --- Helper component to overlay ATC script on the Text canvas --- */
  const TextCanvasWithOverlay: React.FC<{
    slide: Slide;
    isPreview: boolean;
    atcAutoplay?: boolean;
    scriptPreview?: boolean;
  }> = ({ slide, isPreview, atcAutoplay, scriptPreview }) => {
    const hostRef = useRef<HTMLDivElement | null>(null);
    const [box, setBox] = useState<{ w: number; h: number }>({
      w: 800,
      h: 600,
    });

    useEffect(() => {
      if (!hostRef.current) return;
      const el = hostRef.current;
      const measure = () => {
        const r = el.getBoundingClientRect();
        setBox({ w: Math.max(10, r.width), h: Math.max(10, r.height) });
      };
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      measure();
      return () => ro.disconnect();
    }, [slide.id]);

    const script = getScript(slide);
    const hasScript = script.length > 0;
    const hasText = !!slide.body_html?.trim();

    const LINE_HEIGHT = 140;
    const contentHeight = Math.max(box.h, script.length * LINE_HEIGHT);
    return (
      <div
        ref={hostRef}
        className="text-area-canvas text-start p-4 position-relative"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {!hasScript && hasText && (
          <div
            style={{
              flex: 1,
              overflowY: "auto",
            }}
            className="rendered-text"
            dangerouslySetInnerHTML={{ __html: slide.body_html }}
          />
        )}

        {hasScript && (
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              position: "relative",
            }}
          >
            <svg
              viewBox={`0 0 ${box.w} ${contentHeight}`}
              preserveAspectRatio="xMidYMid meet"
              style={{
                width: "100%",
                height: "auto",
                display: "block",
              }}
            >
              <ScriptOverlaySvg
                vb={{ w: box.w, h: box.h }}
                script={script}
                autoplay={isPreview ? !!atcAutoplay : !!scriptPreview}
                visible={true}
              />
            </svg>
          </div>
        )}

        {!isPreview && !hasScript && !hasText && (
          <div className="text-muted text-center">Add text or ATC script →</div>
        )}
      </div>
    );
  };

  /* ---------------- Version loading + actions (WebService) ---------------- */

  const loadDirectVersion = async () => {
    if (!lessonUuid) return;
    setLoading(true);
    try {
      if (viewVersion === "published") {
        const res: any = await WebService.getAPI({
          action: `/lessons/${lessonUuid}/question-versions/active`,
        });
        const active = res?.active_version;
        setIsStageChecker(res.is_stage_checker || false);
        setImageBaseUrl(res.image_base_url || "");
        if (active?.json) {
          setDraft(normalizeDraft(active.json));

          setCurrentVersionUuid(active.uuid || null);
          setIdx(0);
        } else {
          // no published -> switch to draft view
          setCurrentVersionUuid(null);
          setSearchParams({
            uuid: lessonUuid,
            version: "draft",
            ...(source ? { source } : {}),
          });
        }
      } else {
        const res: any = await WebService.getAPI({
          action: `/lessons/${lessonUuid}/question-versions/draft`,
        });
        const draftVer = res?.draft_version;
        setIsStageChecker(res.is_stage_checker || false);
        setImageBaseUrl(res.image_base_url || "");
        if (draftVer?.json) {
          setDraft(normalizeDraft(draftVer.json));
          setCurrentVersionUuid(draftVer.uuid || null);
          setIdx(0);
        } else {
          setDraft(makeDraft(res.is_stage_checker || false));
          // no draft yet -> keep local editor state
          setCurrentVersionUuid(null);
        }
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // published = view only
    if (viewVersion === "published") {
      setIsEditMode(false);
    }

    // draft = editable
    if (viewVersion === "draft") {
      setIsEditMode(true);
    }
  }, [viewVersion]);
  // Edit published => create a draft (copy of current JSON), switch to draft
  const createDraftFromCurrent = async () => {
    if (!lessonUuid) return;
    const payload = {
      json: draft,
      changelog: "New draft created from published",
    };
    const res: any = await WebService.postAPI({
      action: `/lessons/${lessonUuid}/question-versions`,
      body: payload,
    });
    const newVersion = res?.version;
    if (newVersion?.uuid) {
      setCurrentVersionUuid(newVersion.uuid);
      setSearchParams({
        uuid: lessonUuid,
        version: "draft",
        ...(source ? { source } : {}),
      });
    }
  };

  // Publish current draft
  const publishCurrentDraft = async () => {
    if (!lessonUuid || !currentVersionUuid) return;
    const res: any = await WebService.postAPI({
      action: `/lessons/${lessonUuid}/question-versions/${currentVersionUuid}/publish`,
      body: {},
    });
    //  setSearchParams({ uuid: lessonUuid, version: "published" });
    const levelUuid = res.level_uuid;
    setIsDirty(false);
    // Simulator questions must return to Manage Simulators; trainer questions
    // go back to their lesson list.
    if (source === "simulator") {
      navigate("/manage-simulators");
    } else {
      navigate(`/lessons/${levelUuid}`);
    }
  };

  useEffect(() => {
    loadDirectVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonUuid, viewVersion]);

  useEffect(() => {
    if (slideResponse?.type !== "voice") return;

    // seed UI rows from response
    const ek = (slideResponse.evaluation?.expected_keywords || [])
      .map((s) => s.any_of || [])
      .filter((arr) => arr.length > 0)
      .map((arr) => arr.join(" / "));

    const promptsFromEval = (slideResponse as any).evaluation?.prompt_list as
      | string[]
      | undefined;
    const firstPrompt = slideResponse.prompt || "";
    const combinedPrompts = (
      promptsFromEval && promptsFromEval.length
        ? promptsFromEval
        : [firstPrompt]
    ).filter(Boolean);

    const rows: VoiceSet[] = [];
    const maxLen = Math.max(combinedPrompts.length, ek.length, 1);
    for (let i = 0; i < maxLen; i++) {
      rows.push({
        prompt: combinedPrompts[i] || "",
        expected: ek[i] || "",
      });
    }
    setVoiceSets((m) => ({ ...m, [slide.id]: rows }));
  }, [slide.id, slideResponse?.type]);

  const ensureId = (val?: string | null) =>
    val && String(val).trim().length > 0 ? val : HelperService.generateUUID();

  const makeUnique = <T extends { id: string }>(
    items: T[],
    idGen = () => HelperService.generateUUID(),
  ): T[] => {
    const seen = new Set<string>();
    return items.map((it) => {
      let id = ensureId(it.id);
      if (seen.has(id)) id = idGen();
      seen.add(id);
      return { ...it, id };
    });
  };
  /* ---------------- UUID sanitization before save ---------------- */
  const sanitizeDraftIds = (draftIn: QuestionDraft): QuestionDraft => {
    const d = structuredClone(draftIn);

    /* ---------- Slides (and nested: airplanes, script lines) ---------- */
    d.slides = makeUnique(
      d.slides.map((s) => {
        const out = { ...s };

        // airplanes
        if ((out as any).airplanes?.length) {
          (out as any).airplanes = makeUnique(
            (out as any).airplanes.map((ap: AirplaneEntity) => ({
              ...ap,
              id: ensureId(ap.id),
            })),
          );
        }

        // script lines
        const script = ((out as any).script || []) as Array<{
          id?: string;
          heading?: string;
        }>;
        if (script.length) {
          (out as any).script = makeUnique(
            script.map((line, idx2) => ({
              ...line,
              delay_ms: 0,
              tts: false,
              id: ensureId((line as any).id),
              heading: (line as any).heading || `Section ${idx2 + 1}`,
            })),
          );
        }

        return { ...out, id: ensureId(out.id) };
      }),
    );

    /* Build a fast lookup of valid slide ids for response anchoring */
    const validSlideIds = new Set(d.slides.map((s) => s.id));
    const firstSlideId = d.slides[0]?.id || HelperService.generateUUID();

    /* ---------- Task steps / assets ---------- */
    if (d.task_steps?.length) {
      d.task_steps = makeUnique(
        d.task_steps.map((ts) => ({ ...ts, id: ensureId(ts.id) })),
      );
    }
    if (d.assets?.length) {
      d.assets = makeUnique(
        d.assets.map((a: Asset) => ({ ...a, id: ensureId(a.id) })),
      );
    }

    /* ---------- Responses (and nested: options) ---------- */
    if (d.responses?.length) {
      d.responses = makeUnique(
        d.responses.map((r) => {
          const newR: Response = {
            ...r,
            id: ensureId(r.id),
            anchored_to_slide: validSlideIds.has(r.anchored_to_slide || "")
              ? (r.anchored_to_slide as string)
              : firstSlideId,
          };

          if (newR.type === "select") {
            const rr = newR as ResponseSelect;
            rr.options = makeUnique(
              (rr.options || []).map((o) => ({ ...o, id: ensureId(o.id) })),
            );
            return rr;
          }

          return newR;
        }),
      );
    }

    return d;
  };

  type VoiceSet = { prompt: string; expected: string }; // expected = "a / b / c"
  const [voiceSets, setVoiceSets] = useState<Record<string, VoiceSet[]>>({});

  /* ---------------- JSX ---------------- */
  const feedback = canvasFeedback[slide.id] || "";
  const hasAtc =
    !!(slide as any).atc?.text?.trim() ||
    !!atcText[slide.id]?.trim() ||
    getScript(slide).length > 0;
  const submitted = !!submittedMap[slide.id];
  const isCanvasPreview = isPreview && isCanvasAction;
  const [atcOpacity, setAtcOpacity] = useState<Record<string, number>>({});
  const getAtc = (s: Slide) =>
    (s as any).atc ?? {
      text: "",
      autoplay: false,
      author_preview: false,
      opacity: 0,
    };

  const setAtc = (s: Slide, patchAtc: Partial<any>) => {
    (s as any).atc = { ...getAtc(s), ...patchAtc };
  };

  // duplicate current slide (use UUID instead of S#)
  const duplicateSlide = () => {
    patch((d) => {
      const cur = d.slides[idx];
      const copy = structuredClone(cur);
      const newId = HelperService.generateUUID();
      copy.id = newId;
      d.slides.splice(idx + 1, 0, copy);
    });
    setIdx(idx + 1);
  };

  const syncVoiceSetsToResponse = (rows: VoiceSet[]) => {
    const expected_keywords = rows
      .map((r) =>
        (r.expected || "")
          .split("/")
          .map((x) => x.trim())
          .filter(Boolean),
      )
      .filter((arr) => arr.length > 0)
      .map((arr) => ({ any_of: arr }));

    const prompt_list = rows
      .map((r) => r.prompt)
      .filter((p) => p?.trim().length);
    const primaryPrompt = prompt_list[0] || "";

    mutateResponse((r) => {
      const base = r as ResponseVoice;
      return {
        ...base,
        prompt: primaryPrompt, // keep compatibility
        evaluation: {
          ...base.evaluation,
          strategy: "voice_asr",
          expected_keywords,
          // store full list so you can use later (e.g., feedback popup as image)
          prompt_list,
        },
      } as ResponseVoice;
    });
  };

  const gid = stageChecker && groupFilter !== "ALL" ? groupFilter : "";

  return (
    <div className="dashboard-content instruction-screen">
      {/* header */}
      <Row className="mb-3 align-items-center justify-content-between">
        <Col lg={6} className="d-flex align-items-center">
          <button
            type="button"
            onClick={() => {
              navigate(-1);
            }}
            className="text-dark me-2 btn btn-link p-0"
            aria-label="Back"
            style={{
              textDecoration: "none",
              background: "none",
              border: "none",
            }}
          >
            <IoArrowBack size={22} />
          </button>
          <h5 className="fw-bold mb-0">
            Slides{" "}
            <span className="muted">
              ({visiblePos + 1} of{" "}
              {stageChecker && groupFilter !== "ALL"
                ? visibleIdxs.length
                : draft.slides.length}
              )
            </span>{" "}
            <Badge bg={slide.kind === "instruction" ? "secondary" : "primary"}>
              {slide.kind}
            </Badge>
          </h5>

          {/* <div className="d-flex align-items-center gap-3 ml-5 mt-1 text-muted small">
            {meta?.call_sign && (
              <span style={{ marginLeft: "9px" }}>
                Call Sign: <strong>{String(meta.call_sign)}</strong>
              </span>
            )}
            {meta?.atis && (
              <span>
                ATIS: <strong>{String(meta.atis)}</strong>
              </span>
            )}
            {meta?.altitude && (
              <span>
                Altitude: <strong>{String(meta.altitude)}</strong>
              </span>
            )}

            <Button
              size="sm"
              style={{ marginLeft: "12px" }}
              variant="outline-secondary"
              disabled={!isEditMode}
              onClick={() => setMetaOpen(true)}
              className=""
            >
              <FaEdit />
            </Button>
          </div> */}
        </Col>
        <Col lg={6} className="text-end">
          {!isPreview ? (
            <div className="d-inline-flex gap-2">
              {!stageChecker && (
                <Button
                  size="sm"
                  variant="light"
                  className="border"
                  onClick={() => {
                    const id = HelperService.generateUUID(); // UUID instead of S#
                    patch((d) =>
                      d.slides.splice(idx + 1, 0, {
                        id,
                        kind: "instruction",
                        title: "",
                        body_html: "",
                        media: [],
                        layout: "image_top",
                        expected_time_sec: 0,
                      }),
                    );
                    setIdx(idx + 1);
                  }}
                >
                  <FaAlignLeft className="me-2" /> Add Instruction
                </Button>
              )}
              <Button
                size="sm"
                variant="light"
                className="border"
                onClick={() => {
                  const id = HelperService.generateUUID(); // UUID instead of S#
                  patch((d) =>
                    d.slides.splice(idx + 1, 0, {
                      id,
                      kind: "question",
                      title: "",
                      body_html: "",
                      media: [],
                      layout: "image_top",
                      expected_time_sec: 0,
                      airplanes: [],
                      ...(gid ? ({ group_id: gid } as any) : {}),
                    }),
                  );
                  setIdx(idx + 1);
                }}
              >
                <FaPaperPlane className="me-2" /> Add Question
              </Button>
              {stageChecker && !isPreview && (
                <Form.Select
                  size="sm"
                  style={{ width: 140 }}
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                >
                  <option value="ALL">All</option>
                  {GROUPS.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </Form.Select>
              )}
              <div className="ms-2">
                <Button
                  size="sm"
                  variant="dark"
                  onClick={() => setPreviewMode("author")}
                >
                  Author
                </Button>
                {/* <Button
                  size="sm"
                  variant="light"
                  onClick={() => setPreviewMode("preview")}
                >
                  Preview
                </Button> */}

                <Button
                  size="sm"
                  variant="light"
                  className="border ml-2"
                  onClick={() => setOutlineOpen(true)}
                  title="Open outline"
                >
                  Question Outline
                </Button>
              </div>

              {/* NEW: Edit/Publish controls (do not alter other UI) */}
              {viewVersion === "published" && (
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="ms-2"
                  onClick={async () => {
                    await createDraftFromCurrent();
                    setIsEditMode(true); // 🔥 THIS WAS MISSING
                  }}
                  disabled={loading}
                  title="Create a new draft from this published version"
                >
                  Edit
                </Button>
              )}
              {viewVersion === "draft" && (
                <Button
                  size="sm"
                  variant="primary"
                  className="ms-2"
                  onClick={publishCurrentDraft}
                  disabled={loading || !currentVersionUuid}
                  title="Publish draft"
                >
                  Publish
                </Button>
              )}
            </div>
          ) : (
            <div className="d-inline-flex gap-2">
              <div className="ms-2">
                <Button
                  size="sm"
                  variant="light"
                  onClick={() => setPreviewMode("author")}
                >
                  Author
                </Button>
                <Button
                  size="sm"
                  variant="dark"
                  onClick={() => setPreviewMode("preview")}
                >
                  Preview
                </Button>
              </div>
            </div>
          )}
        </Col>
      </Row>

      <Row>
        {/* LEFT */}
        <Col lg={isPreview ? 12 : 8}>
          {!isPreview && (
            <div className="display-type-tabs mb-3 d-flex gap-2 align-items-center">
              <Button
                variant={slide.layout === "image_top" ? "dark" : "light"}
                onClick={() => updateSlide({ layout: "image_top" })}
              >
                <FaImages className="me-2" /> Image
              </Button>
              <Button
                variant={slide.layout === "image_left" ? "dark" : "light"}
                onClick={() => updateSlide({ layout: "image_left" })}
              >
                Double Image
              </Button>
              <Button
                variant={slide.layout === "image_only" ? "dark" : "light"}
                onClick={() => updateSlide({ layout: "image_only" })}
              >
                Text
              </Button>
              <div className="flex-grow-1" />
              {/* slide.kind === "question" && */}
              {!isPreview && displayType !== "double" && (
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={addAirplane}
                  >
                    <FaPaperPlane className="me-2" /> Add Airplane
                  </Button>
                  {/* {activeAp && (
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => removeAirplane(activeAp)}
                    >
                      Remove Selected ({activeAp})
                    </Button>
                  )} */}
                  {(slide.airplanes?.length ?? 0) > 0 && (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={removeAllAirplanes}
                    >
                      Remove All
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Canvas */}
          <div
            className={`canvas-container ${isPreview ? "canvas-large" : ""}`}
          >
            <div className="canvas-stage position--relative">
              <AtcDimLayer opacity={atcOpacity[slide.id] ?? 0} />
              {slide.layout !== "image_only" && (
                <>
                  {displayType === "image" && (
                    <div className="single-image-wrap">
                      {slide.media?.[0] ? (
                        <div
                          className="image-preview-container"
                          style={{
                            width: "100%",
                            height: "100%",
                            position: "relative",
                          }}
                        >
                          {!imageLoaded && (
                            <div className="image-loader">Loading image…</div>
                          )}

                          {imageLoaded && renderSvgCanvas()}

                          {!isPreview && (
                            <button
                              className="remove-image-btn"
                              onClick={() => removeMediaAt(0)}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ) : (
                        <UploadDropzone
                          onUpload={(url) => setMediaAt(0, url)}
                        />
                      )}
                    </div>
                  )}

                  {displayType === "double" && (
                    <div className="double-image-wrap">
                      {/* LEFT HALF — interactive (planes/canvas) on media[0] */}
                      <div className="half">
                        {slide.media?.[0] ? (
                          <div
                            className="image-preview-container"
                            style={{
                              width: "100%",
                              height: "100%",
                              position: "relative",
                            }}
                          >
                            {!leftLoaded && (
                              <div className="image-loader">Loading image…</div>
                            )}

                            {leftLoaded && (
                              <img
                                className="preview-image contain"
                                src={leftSrc}
                                alt="Left"
                              />
                            )}
                            {!isPreview && (
                              <button
                                className="remove-image-btn"
                                onClick={() => removeMediaAt(0)}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ) : (
                          <UploadDropzone
                            onUpload={(url) => setMediaAt(0, url)}
                          />
                        )}
                      </div>

                      {/* RIGHT HALF — simple image preview for media[1] */}
                      <div className="half">
                        {slide.media?.[1] ? (
                          <div
                            className="image-preview-container"
                            style={{
                              width: "100%",
                              height: "100%",
                              position: "relative",
                            }}
                          >
                            {!rightLoaded && (
                              <div className="image-loader">Loading image…</div>
                            )}

                            {rightLoaded && (
                              <img
                                className="preview-image contain"
                                src={rightSrc}
                                alt="Right"
                              />
                            )}
                            {!isPreview && (
                              <button
                                className="remove-image-btn"
                                onClick={() => removeMediaAt(1)}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ) : (
                          <UploadDropzone
                            onUpload={(url) => setMediaAt(1, url)}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {slide.layout === "image_only" && (
                <TextCanvasWithOverlay
                  slide={slide}
                  isPreview={isPreview}
                  atcAutoplay={atcAutoplay[slide.id]}
                  scriptPreview={scriptPreview[slide.id]}
                />
              )}
            </div>
            <div id="check-banner" className="check-banner" />
          </div>

          {/* Footer */}
          <div className="canvas-footer">
            <div className="footer-progress">
              {isCanvasPreview ? (
                <>
                  <div
                    className="progress-track"
                    style={{ visibility: "hidden" }}
                  >
                    <span className="progress-start">0</span>
                    <ProgressBar now={0} className="mx-2 flex-grow-1" />
                    <span className="progress-end">100</span>
                  </div>
                  {!submitted && (
                    <div
                      className="d-flex justify-content-center my-1"
                      style={{ width: "100%" }}
                    >
                      <Button
                        variant="primary"
                        onClick={handleSubmitCanvas}
                        disabled={planeSelected.length === 0}
                        style={{
                          minWidth: 220,
                          height: 46,
                          fontSize: 16,
                          fontWeight: 700,
                        }}
                      >
                        Submit
                      </Button>
                    </div>
                  )}
                  <div
                    className="progress-label"
                    style={{ fontWeight: 700, fontSize: 16, minHeight: 22 }}
                  >
                    {submitted
                      ? feedback === "correct"
                        ? "✅ Correct"
                        : "❌ Incorrect"
                      : ""}
                  </div>
                  <div className="progress-label">
                    {atcShown && atcLabelText ? (
                      <span className="reveal-answer atc-text">
                        {atcLabelText}
                      </span>
                    ) : resShown ? (
                      <span className="reveal-answer">
                        {responseLabelText || "Answer"}
                      </span>
                    ) : (
                      (slide?.title || "").trim() ||
                      (slide.kind === "instruction"
                        ? "Instruction"
                        : "Question")
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="progress-track">
                    <span className="progress-start">0</span>
                    <ProgressBar
                      now={((idx + 1) / draft.slides.length) * 100}
                      className="mx-2 flex-grow-1"
                    />
                    <span className="progress-end">100</span>
                  </div>
                  <div className="progress-label">
                    {atcShown && atcLabelText ? (
                      <span className="reveal-answer atc-text">
                        {atcLabelText}
                      </span>
                    ) : resShown ? (
                      <span className="reveal-answer">
                        {responseLabelText || "Answer"}
                      </span>
                    ) : (
                      (slide?.title || "").trim() ||
                      (slide.kind === "instruction"
                        ? "Instruction"
                        : "Question")
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="footer-icons">
              {/* ATC button: toggle the ATC text in the label + play audio (first click) */}
              <button
                className="tap-eye-btn"
                onClick={() => {
                  const showing = atcShown;
                  // toggle the ATC text shown in the label
                  setAtcShowMap((m) => ({ ...m, [slide.id]: !showing }));
                  if (showing) return; // second click just hides it (no audio)

                  const script = getScript(slide);
                  if (script.length) {
                    const pick = script.some((l) => l.tts)
                      ? script.filter((l) => l.tts).map((l) => l.text)
                      : script.map((l) => l.text);
                    speakSequence(pick, 1.0, 0.9);
                  } else {
                    playATC((slide as any).atc?.text);
                  }
                }}
                disabled={!hasAtc}
                title="ATC"
              >
                <FaMicrophone size={16} />
                <span className="tap-eye-label">ATC</span>
              </button>

              {/* Pager pill: prev / ? / next */}
              <div className="tap-pager">
                <button
                  className="tap-pager-arrow"
                  onClick={prev}
                  title="Previous"
                >
                  <IoArrowBackOutline size={20} />
                </button>
                <button
                  className="tap-pager-help"
                  onClick={checkAnswer}
                  title="Check"
                  // disabled={isCanvasPreview && planeSelected.length === 0}
                  disabled
                >
                  <FaQuestionCircle size={16} />
                </button>
                <button
                  className="tap-pager-arrow"
                  onClick={next}
                  title="Next"
                >
                  <IoArrowForwardOutline size={20} />
                </button>
              </div>

              <button
                className="tap-eye-btn"
                onClick={() =>
                  setRevealMap((m) => ({ ...m, [slide.id]: !m[slide.id] }))
                }
                title="Reveal"
              >
                <FaEye size={16} />
                <span className="tap-eye-label">RES</span>
              </button>

              {isPreview && slideResponse?.type === "voice" && (
                <button
                  className="icon-btn mic-icon"
                  onClick={() => {
                    if (voiceMap[slide.id]?.recording) stopRecord();
                    else startRecord();
                  }}
                >
                  <FaMicrophone size={16} />
                </button>
              )}

              {/* Duplicate current slide (unchanged) */}
              {!isPreview && (
                <button
                  className="icon-btn dup-icon"
                  onClick={duplicateSlide}
                  title="Duplicate slide"
                >
                  <FaClone size={16} />
                </button>
              )}

              {!isPreview && (
                <button
                  className="icon-btn delete-icon"
                  onClick={() => {
                    if (draft.slides.length <= 1) return;
                    const removedId = slide.id;
                    patch((d) => {
                      d.responses = d.responses.filter(
                        (r) => r.anchored_to_slide !== removedId,
                      );
                      d.slides.splice(idx, 1);
                    });
                    setIdx(Math.max(0, idx - 1));
                  }}
                >
                  <FaTrashAlt size={15} />
                </button>
              )}
            </div>
          </div>

          {/* {!isPreview && (
            <div className="json-preview mt-3">
              <h6>Final JSON Preview</h6>
              <pre>{JSON.stringify(draft, null, 2)}</pre>
            </div>
          )} */}
        </Col>

        {/* RIGHT (authoring) */}
        {!isPreview && (
          <Col lg={4}>
            <fieldset disabled={!isEditMode}>
              <div className="instruction-form">
                <Row className="mb-3">
                  <Col>
                    <Form.Label>Kind</Form.Label>
                    <Form.Select
                      value={slide.kind}
                      onChange={(e) =>
                        updateSlide({ kind: e.target.value as Slide["kind"] })
                      }
                    >
                      {!stageChecker && (
                        <option value="instruction">instruction</option>
                      )}
                      <option value="question">question</option>
                    </Form.Select>
                  </Col>
                </Row>

                {stageChecker && slide.kind === "question" && (
                  <Form.Group className="mb-3">
                    <Form.Label>Group</Form.Label>
                    <Form.Select
                      value={getGroupId(slide) || ""}
                      onChange={(e) => setGroupIdOnCurrentSlide(e.target.value)}
                    >
                      <option value="">None</option>
                      {GROUPS.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={slide.title}
                    onChange={(e) => updateSlide({ title: e.target.value })}
                  />
                </Form.Group>

                {displayType === "text" && (
                  <Form.Group className="mb-3">
                    <Form.Label>Body (HTML)</Form.Label>
                    <div
                      style={{
                        pointerEvents: !isEditMode ? "none" : "auto",
                        opacity: !isEditMode ? 0.6 : 1,
                      }}
                    >
                      <RichTextEditor
                        value={slide.body_html}
                        onChange={(e) => updateSlide({ body_html: e })}
                        height={"auto"}
                      />
                    </div>
                  </Form.Group>
                )}

                {/* Legacy ATC */}
                <div className="light-panel mb-3">
                  <div className="d-flex justify-content-end align-items-center">
                    {/* <strong>ATC Message</strong>
                  <Form.Check
                    style={{ marginLeft: "30%" }}
                    type="switch"
                    label="Author Preview"
                    checked={getAtc(slide).author_preview}
                    onChange={(e) =>
                      patch((d) => {
                        setAtc(d.slides[idx], {
                          author_preview: e.target.checked,
                        });
                      })
                    }
                  /> */}
                    <Form.Check
                      type="switch"
                      id="atc-auto"
                      label="Autoplay"
                      checked={getAtc(slide).autoplay}
                      onChange={(e) =>
                        patch((d) => {
                          setAtc(d.slides[idx], { autoplay: e.target.checked });
                        })
                      }
                    />
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    className="mt-2"
                    placeholder="Enter ATC phrase"
                    value={getAtc(slide).text}
                    onChange={(e) =>
                      patch((d) => {
                        setAtc(d.slides[idx], { text: e.target.value });
                      })
                    }
                  />
                  <Form.Group className="mt-2">
                    <Form.Label>
                      Script Background Blur
                      <span className="text-muted ms-2">
                        ({Math.round((atcOpacity[slide.id] ?? 0) * 100)}%)
                      </span>
                    </Form.Label>

                    <Form.Range
                      min={0}
                      max={1}
                      step={0.05}
                      value={atcOpacity[slide.id] ?? 0}
                      onChange={(e) =>
                        setAtcOpacity((m) => ({
                          ...m,
                          [slide.id]: Number(e.target.value),
                        }))
                      }
                    />
                  </Form.Group>

                  {/* Legacy Script */}
                  <div className="d-flex justify-content-between align-items-center mt-2 mb-1">
                    <strong>ATC Script</strong>
                  </div>

                  {(getScript(slide) || []).map((line, i) => (
                    <Row className="g-2 align-items-center mt-2" key={line.id}>
                      <Col xs={3}>
                        <Form.Select
                          size="sm"
                          value={line.role}
                          onChange={(e) =>
                            patch((d) => {
                              const s = d.slides[idx];
                              const arr = getScript(s).slice();
                              arr[i] = {
                                ...arr[i],
                                role: e.target.value as AtcLine["role"],
                              };
                              setScript(s, arr);
                            })
                          }
                        >
                          <option value="ATC">ATC</option>
                          <option value="PILOT">PILOT</option>
                        </Form.Select>
                      </Col>

                      <Col xs={9}>
                        <Form.Control
                          value={line.message || ""}
                          placeholder="Message"
                          onChange={(e) =>
                            patch((d) => {
                              const s = d.slides[idx];
                              const arr = getScript(s).slice();
                              arr[i] = {
                                ...arr[i],
                                message: e.target.value, // ✅ message
                              };
                              setScript(s, arr);
                            })
                          }
                        />
                      </Col>

                      {/* Line text — full width (wraps to its own row) */}
                      <Col xs={12}>
                        <Form.Control
                          size="sm"
                          value={line.text}
                          placeholder="Line text…"
                          onChange={(e) =>
                            patch((d) => {
                              const s = d.slides[idx];
                              const arr = getScript(s).slice();
                              arr[i] = { ...arr[i], text: e.target.value };
                              setScript(s, arr);
                            })
                          }
                        />
                      </Col>

                      {/* <Col xs={2}>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={line.delay_ms ?? 0}
                        placeholder="Delay ms"
                        onChange={(e) =>
                          patch((d) => {
                            const s = d.slides[idx];
                            const arr = getScript(s).slice();
                            arr[i] = {
                              ...arr[i],
                              delay_ms: Number(e.target.value || 0),
                            };
                            setScript(s, arr);
                          })
                        }
                      />
                    </Col> */}

                      {/* <Col xs="auto">
                      <Form.Check
                        type="checkbox"
                        label="TTS"
                        checked={!!line.tts}
                        onChange={(e) =>
                          patch((d) => {
                            const s = d.slides[idx];
                            const arr = getScript(s).slice();
                            arr[i] = { ...arr[i], tts: e.target.checked };
                            setScript(s, arr);
                          })
                        }
                      />
                    </Col> */}

                      <Col xs="auto">
                        <Form.Select
                          size="sm"
                          value={line.align ?? "left"}
                          onChange={(e) =>
                            patch((d) => {
                              const s = d.slides[idx];
                              const arr = getScript(s).slice();
                              const { x, y, ...rest } = arr[i] as any; // keep existing cleanup
                              arr[i] = {
                                ...rest,
                                align: e.target.value as "left" | "right",
                              };
                              setScript(s, arr);
                            })
                          }
                        >
                          <option value="left">Left</option>
                          <option value="right">Right</option>
                        </Form.Select>
                      </Col>

                      <Col
                        xs="auto"
                        className="d-flex align-items-center gap-2"
                      >
                        <Form.Check
                          type="radio"
                          name={`style-${line.id}`}
                          id={`style-line-${line.id}`}
                          label="Line"
                          checked={(line.style ?? "line") === "line"}
                          onChange={() =>
                            patch((d) => {
                              const s = d.slides[idx];
                              const arr = getScript(s).slice();
                              arr[i] = { ...arr[i], style: "line" };
                              setScript(s, arr);
                            })
                          }
                        />
                        <Form.Check
                          type="radio"
                          name={`style-${line.id}`}
                          id={`style-arrow-${line.id}`}
                          label="Talking Arrow"
                          checked={(line.style ?? "line") === "arrow"}
                          onChange={() =>
                            patch((d) => {
                              const s = d.slides[idx];
                              const arr = getScript(s).slice();
                              arr[i] = { ...arr[i], style: "arrow" };
                              setScript(s, arr);
                            })
                          }
                        />
                      </Col>

                      <Col className="d-flex justify-content-end">
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() =>
                            patch((d) => {
                              const s = d.slides[idx];
                              const arr = getScript(s).slice();
                              arr.splice(i, 1);
                              setScript(s, arr);
                            })
                          }
                        >
                          Del
                        </Button>
                      </Col>
                    </Row>
                  ))}

                  <Button
                    size="sm"
                    variant="light"
                    className="border mt-2"
                    onClick={() =>
                      patch((d) => {
                        const s = d.slides[idx];
                        const arr = getScript(s).slice();
                        arr.push({
                          id: HelperService.generateUUID(), // UUID instead of L#
                          role: "ATC",
                          text: "",
                          delay_ms: 400,
                          message: "",
                          tts: false,
                          align: "left",
                          style: "line",
                          heading: `Section ${arr.length + 1}`, // NEW
                          expected_any_of: [], // NEW
                        });
                        setScript(s, arr);
                      })
                    }
                  >
                    + Add line
                  </Button>
                </div>

                {/* Top Bar (info bar) — CALL SIGN / ATIS / ALTITUDE / AIRCRAFT TYPE.
                    Lesson-level: same for every slide. On the website the bar is
                    always shown in the simulator; in training it shows ONLY when
                    "Show top bar in training" is checked. */}
                <div className="light-panel mb-3">
                  {/* Title + checkbox on the same line (compact) */}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Top Bar (Info Bar)</strong>
                    <Form.Check
                      type="checkbox"
                      id="show-info-bar"
                      className="mb-0 small"
                      label="Show top bar in training"
                      checked={!!barCfg.show_info_bar}
                      onChange={(e) =>
                        setBarField("show_info_bar", e.target.checked)
                      }
                    />
                  </div>

                  {/* Lesson-level: one global bar (off) vs a per-page bar (on) */}
                  <Form.Check
                    type="checkbox"
                    id="per-page-bar"
                    className="small mb-2"
                    label="Different bar content per page"
                    checked={perPageBar}
                    onChange={(e) =>
                      patch((d) => {
                        (d.runtime_params as any).per_page_bar =
                          e.target.checked;
                      })
                    }
                  />

                  {/* Field order follows the bar: Call Sign, Type, ATIS, Altitude */}
                  <Row className="g-2">
                    <Col xs={6}>
                      <Form.Label className="small mb-1">Call Sign</Form.Label>
                      <Form.Control
                        size="sm"
                        value={(barCfg.call_sign as string) || ""}
                        placeholder="e.g. HARRIER 207"
                        onChange={(e) =>
                          setBarField("call_sign", e.target.value)
                        }
                      />
                    </Col>

                    <Col xs={6}>
                      <Form.Label className="small mb-1">Type</Form.Label>
                      <Form.Control
                        size="sm"
                        value={(barCfg.aircraft_type as string) || ""}
                        placeholder="e.g. C172"
                        onChange={(e) =>
                          setBarField("aircraft_type", e.target.value)
                        }
                      />
                    </Col>

                    <Col xs={6}>
                      <Form.Label className="small mb-1">
                        ATIS (1 letter)
                      </Form.Label>
                      <Form.Control
                        size="sm"
                        maxLength={1}
                        value={(barCfg.atis as string) || ""}
                        placeholder="A"
                        onChange={(e) =>
                          setBarField("atis", e.target.value.toUpperCase())
                        }
                      />
                    </Col>

                    <Col xs={6}>
                      <Form.Label className="small mb-1">Altitude</Form.Label>
                      <Form.Control
                        size="sm"
                        value={(barCfg.altitude as string) || ""}
                        placeholder="e.g. 2000"
                        onChange={(e) =>
                          setBarField("altitude", e.target.value)
                        }
                      />
                    </Col>
                  </Row>

                  {perPageBar && (
                    <div className="text-muted small mt-2">
                      Editing this page only. Turn off to use one bar for every
                      page.
                    </div>
                  )}
                </div>

                {/* Airplanes panel (unchanged logic) */}
                {slide.kind === "question" && (
                  <div className="light-panel mb-3">
                    <strong>Airplanes</strong>
                    {(slide.airplanes || []).length === 0 ? (
                      <div className="text-muted small mt-2">
                        No airplanes yet. Use “Add Airplane”.
                      </div>
                    ) : (
                      <div className="mt-2">
                        {(slide.airplanes || []).map((ap) => {
                          const isHotspot =
                            slideResponse?.type === "canvas_action" &&
                            (slideResponse as ResponseCanvas).evaluation
                              ?.strategy === "hotspot_select";
                          const correctIds = isHotspot
                            ? new Set(
                                (slideResponse as ResponseCanvas).evaluation
                                  ?.correct_entity_ids || [],
                              )
                            : new Set<string>();
                          const checked = correctIds.has(ap.id);

                          return (
                            <Row
                              key={ap.id}
                              className="g-2 align-items-center mb-2"
                            >
                              <Col xs="auto">
                                <span
                                  className="badge bg-light text-dark"
                                  style={{
                                    minWidth: 46,
                                    display: "inline-block",
                                    textAlign: "center",
                                  }}
                                >
                                  {ap.id}
                                </span>
                              </Col>
                              <Col>
                                <Form.Control
                                  type="color"
                                  value={ap.color || "#0b3fad"}
                                  title="Color"
                                  onChange={(e) =>
                                    patch((d) => {
                                      const s = d.slides[idx];
                                      s.airplanes = (s.airplanes || []).map(
                                        (a) =>
                                          a.id === ap.id
                                            ? { ...a, color: e.target.value }
                                            : a,
                                      );
                                    })
                                  }
                                />
                              </Col>
                              <Col xs="auto">
                                <Form.Check
                                  type="checkbox"
                                  label="correct"
                                  disabled={!isHotspot}
                                  checked={checked}
                                  onChange={(e) =>
                                    mutateResponse((r) => {
                                      if (r.type !== "canvas_action") return r;
                                      const rr = r as ResponseCanvas;
                                      const ev = {
                                        ...(rr.evaluation || {}),
                                        strategy:
                                          rr.evaluation?.strategy ??
                                          "hotspot_select",
                                      };
                                      const list = new Set<string>(
                                        ev.correct_entity_ids || [],
                                      );
                                      if (e.target.checked) list.add(ap.id);
                                      else list.delete(ap.id);
                                      return {
                                        ...rr,
                                        evaluation: {
                                          ...ev,
                                          correct_entity_ids: Array.from(list),
                                        },
                                      };
                                    })
                                  }
                                />
                              </Col>
                              <Col xs="auto">
                                <Button
                                  size="sm"
                                  variant={
                                    activeAp === ap.id
                                      ? "primary"
                                      : "outline-primary"
                                  }
                                  onClick={() => setActiveAp(ap.id)}
                                >
                                  Select
                                </Button>
                              </Col>
                            </Row>
                          );
                        })}
                        {!slideResponse ||
                        slideResponse.type !== "canvas_action" ||
                        (slideResponse as ResponseCanvas).evaluation
                          ?.strategy !== "hotspot_select" ? (
                          <div className="text-muted small">
                            Tip: set Response → Canvas action → “Select
                            airplane(s)” to enable “correct” checkboxes.
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                )}

                {/* Response panel */}
                {slide.kind === "question" && (
                  <div className="light-panel mb-3">
                    <strong>Response</strong>
                    <div className="d-flex gap-2 mt-2">
                      <Form.Select
                        value={slideResponse?.type || ""}
                        onChange={(e) =>
                          setResponseType(
                            (e.target.value ||
                              "canvas_action") as Response["type"],
                          )
                        }
                      >
                        <option value="">Choose type…</option>
                        {/* <option value="select">Multiple choice</option>
                      <option value="text">Text</option>
                      <option value="numeric">Numeric</option> */}
                        <option value="voice">Voice</option>
                        <option value="canvas_action">Canvas action</option>
                      </Form.Select>
                    </div>

                    {slideResponse?.type && (
                      <div className="mt-3">
                        {slideResponse.type === "select" && (
                          <>
                            {(slideResponse as ResponseSelect).options?.map(
                              (o, i) => (
                                <Row
                                  className="g-2 align-items-center mb-2"
                                  key={o.id || i}
                                >
                                  <Col>
                                    <Form.Control
                                      value={o.label}
                                      onChange={(e) =>
                                        mutateResponse((r) => {
                                          const rr = r as ResponseSelect;
                                          const opts =
                                            rr.options?.slice() || [];
                                          opts[i] = {
                                            ...opts[i],
                                            label: e.target.value,
                                          };
                                          return { ...rr, options: opts };
                                        })
                                      }
                                      placeholder={`Option ${i + 1}`}
                                    />
                                  </Col>
                                  <Col xs="auto">
                                    <Form.Check
                                      type="checkbox"
                                      label="correct"
                                      checked={!!o.is_correct}
                                      onChange={(e) =>
                                        mutateResponse((r) => {
                                          const rr = r as ResponseSelect;
                                          const opts =
                                            rr.options?.slice() || [];
                                          opts[i] = {
                                            ...opts[i],
                                            is_correct: e.target.checked,
                                          };
                                          return { ...rr, options: opts };
                                        })
                                      }
                                    />
                                  </Col>
                                  <Col xs="auto">
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() =>
                                        mutateResponse((r) => {
                                          const rr = r as ResponseSelect;
                                          const opts = (
                                            rr.options || []
                                          ).filter((_, j) => j !== i);
                                          return { ...rr, options: opts };
                                        })
                                      }
                                    >
                                      Del
                                    </Button>
                                  </Col>
                                </Row>
                              ),
                            )}
                            <Button
                              size="sm"
                              variant="light"
                              className="border"
                              onClick={() =>
                                mutateResponse((r) => {
                                  const rr = r as ResponseSelect;
                                  const next = [
                                    ...(rr.options || []),
                                    {
                                      id: HelperService.generateUUID(), // UUID instead of O#
                                      label: "New option",
                                      is_correct: false,
                                    },
                                  ];
                                  return { ...rr, options: next };
                                })
                              }
                            >
                              + Add option
                            </Button>
                          </>
                        )}

                        {slideResponse.type === "voice" && (
                          <div className="border rounded p-2">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong>Prompts & Expected Keywords</strong>
                              <Button
                                size="sm"
                                variant="light"
                                className="border"
                                onClick={() => {
                                  const rows = voiceSets[slide.id] || [];
                                  const next = [
                                    ...rows,
                                    { prompt: "", expected: "" },
                                  ];
                                  setVoiceSets((m) => ({
                                    ...m,
                                    [slide.id]: next,
                                  }));
                                  syncVoiceSetsToResponse(next);
                                }}
                              >
                                + Add
                              </Button>
                            </div>

                            {(voiceSets[slide.id] || []).map((row, i) => (
                              <Row
                                className="g-3 align-items-start mb-3"
                                key={`vs-${i}`}
                              >
                                <Col md={12}>
                                  <Form.Label className="d-block mb-2">
                                    Prompt
                                  </Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={row.prompt}
                                    placeholder="Prompt shown to learner"
                                    onChange={(e) => {
                                      const rows = [
                                        ...(voiceSets[slide.id] || []),
                                      ];
                                      rows[i] = {
                                        ...rows[i],
                                        prompt: e.target.value,
                                      };
                                      setVoiceSets((m) => ({
                                        ...m,
                                        [slide.id]: rows,
                                      }));
                                    }}
                                    onBlur={() =>
                                      syncVoiceSetsToResponse(
                                        voiceSets[slide.id] || [],
                                      )
                                    }
                                  />
                                </Col>

                                <Col md={12}>
                                  <Form.Label className="d-block mb-2">
                                    Expected Keywords (use “/” for alternatives)
                                  </Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={4}
                                    className="mt-0"
                                    size="sm"
                                    value={row.expected}
                                    placeholder={`One set per line; “/” separates alternatives.\nExamples:\ncleared / cleared to land\nrunway / rwy / one six`}
                                    onChange={(e) => {
                                      const rows = [
                                        ...(voiceSets[slide.id] || []),
                                      ];
                                      rows[i] = {
                                        ...rows[i],
                                        expected: e.target.value,
                                      };
                                      setVoiceSets((m) => ({
                                        ...m,
                                        [slide.id]: rows,
                                      }));
                                    }}
                                    onBlur={() =>
                                      syncVoiceSetsToResponse(
                                        voiceSets[slide.id] || [],
                                      )
                                    }
                                  />
                                  <div className="form-text mt-2">
                                    Any one alternative in a line counts as
                                    present.
                                  </div>
                                </Col>

                                <Col md="auto" className="pt-md-4">
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => {
                                      const rows = [
                                        ...(voiceSets[slide.id] || []),
                                      ];
                                      rows.splice(i, 1);
                                      setVoiceSets((m) => ({
                                        ...m,
                                        [slide.id]: rows,
                                      }));
                                      syncVoiceSetsToResponse(rows);
                                    }}
                                  >
                                    Del
                                  </Button>
                                </Col>
                              </Row>
                            ))}
                          </div>
                        )}

                        {slideResponse.type === "canvas_action" && (
                          <>
                            <Form.Group className="mb-2">
                              <Form.Label>Canvas strategy</Form.Label>
                              <Form.Select
                                value={
                                  (slideResponse as ResponseCanvas).evaluation
                                    ?.strategy || "hotspot_select"
                                }
                                onChange={(e) =>
                                  mutateResponse((r) => {
                                    const rr = r as ResponseCanvas;
                                    const ev = {
                                      ...(rr.evaluation || {}),
                                      strategy: e.target.value as any,
                                    };
                                    return { ...rr, evaluation: ev };
                                  })
                                }
                              >
                                <option value="hotspot_select">
                                  Select airplane(s)
                                </option>
                                {/* <option value="hotspot_select_sector">
                                Clock-sector
                              </option>
                              <option value="canvas_target">Pose/range</option> */}
                              </Form.Select>
                            </Form.Group>

                            {(slideResponse as ResponseCanvas).evaluation
                              ?.strategy === "hotspot_select" && (
                              <div className="muted small">
                                Tip: In Preview, click airplanes to select
                                (single-select). Click <strong>RES</strong> to
                                reveal.
                                <div className="mt-2">
                                  {(slide.airplanes || []).map((ap) => {
                                    const arr = new Set(
                                      (slideResponse as ResponseCanvas)
                                        .evaluation?.correct_entity_ids || [],
                                    );
                                    const checked = arr.has(ap.id);
                                    return (
                                      <Form.Check
                                        key={ap.id}
                                        type="checkbox"
                                        id={`corr-${ap.id}`}
                                        label={`Correct: ${ap.id}`}
                                        className="mb-1"
                                        checked={checked}
                                        onChange={(e) =>
                                          mutateResponse((r) => {
                                            const rr = r as ResponseCanvas;
                                            const strategy =
                                              rr.evaluation?.strategy ??
                                              "hotspot_select";
                                            const ev = {
                                              ...(rr.evaluation || {}),
                                              strategy,
                                              correct_entity_ids: [
                                                ...(rr.evaluation
                                                  ?.correct_entity_ids || []),
                                              ],
                                            };
                                            if (e.target.checked)
                                              ev.correct_entity_ids!.push(
                                                ap.id,
                                              );
                                            else
                                              ev.correct_entity_ids =
                                                ev.correct_entity_ids!.filter(
                                                  (id) => id !== ap.id,
                                                );
                                            return { ...rr, evaluation: ev };
                                          })
                                        }
                                      />
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="text-end mt-3">
                  {/* Save only in DRAFT view */}
                  {viewVersion === "draft" && (
                    <Button
                      className="btn-brand-1 px-4"
                      onClick={async () => {
                        // Guard: never persist a blob: preview URL. A blob: URL
                        // lives only in this browser session (an image still
                        // uploading, or a failed upload), so saving it leaves a
                        // dead reference that renders blank in the player /
                        // simulator. Block the save until the real image is in.
                        const blobSlide = (draft.slides || []).findIndex(
                          (s: any) =>
                            (s?.media || []).some(
                              (m: any) =>
                                typeof m === "string" && m.startsWith("blob:")
                            )
                        );
                        if (blobSlide !== -1) {
                          toast.error(
                            `Slide ${blobSlide + 1}: image is still uploading or failed to upload. Re-add the image, then save.`
                          );
                          return;
                        }
                        const sanitized = sanitizeDraftIds(draft);
                        setDraft(sanitized);
                        const res: any = await WebService.postAPI({
                          action: `/lessons/${lessonUuid}/question-versions`,
                          body: { json: sanitized, changelog: "Edited draft" },
                        });
                        const v = res?.version;
                        if (v?.uuid) setCurrentVersionUuid(v.uuid);
                        setIsDirty(false);
                        toast.success("Questions saved as draft");
                      }}
                      disabled={loading}
                    >
                      Save
                    </Button>
                  )}
                </div>
              </div>
            </fieldset>
          </Col>
        )}
      </Row>
      {/* ✅ NEW: Right Outline Offcanvas */}
      <Offcanvas
        show={outlineOpen}
        onHide={() => setOutlineOpen(false)}
        placement="end"
        backdrop
        scroll={false}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Questions Outline</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <Form.Control
            size="sm"
            placeholder="Search by title or id…"
            value={outlineQuery}
            onChange={(e) => setOutlineQuery(e.target.value)}
            className="mb-3"
          />

          {stageChecker ? (
            <Accordion defaultActiveKey={"0"} alwaysOpen>
              {outlineGroups?.order.map((gk, gi) => {
                const items = outlineGroups?.map[gk] || [];

                return (
                  <Accordion.Item key={gk} eventKey={String(gi)}>
                    <Accordion.Header>
                      <div className="d-flex align-items-center justify-content-between w-100">
                        <span className="fw-semibold">{gk}</span>
                        <span className="ms-2 text-muted small">
                          ({items.length})
                        </span>
                      </div>
                    </Accordion.Header>

                    <Accordion.Body
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (!dragSlideId) return;
                        moveQuestionTo(dragSlideId, null, gk, "end"); // drop into group
                        setDragSlideId(null);
                      }}
                      style={{
                        border: "1px dashed rgba(0,0,0,0.12)",
                        borderRadius: 10,
                        padding: 10,
                      }}
                    >
                      {items.length === 0 ? (
                        <div className="text-muted small">
                          Drag a question here to assign it to{" "}
                          <strong>{gk}</strong>.
                        </div>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {items.map(({ s, i }) => {
                            const isActive = draft.slides[idx]?.id === s.id;
                            const label =
                              (s.title || "").trim() || "(Untitled)";

                            return (
                              <div
                                key={s.id}
                                className={`d-flex align-items-center gap-2 px-2 py-2 rounded ${
                                  isActive ? "bg-light" : ""
                                }`}
                                style={{
                                  border: "1px solid rgba(0,0,0,0.08)",
                                }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  if (!dragSlideId || dragSlideId === s.id)
                                    return;
                                  // drop onto a slide => insert before it & assign to that group
                                  moveQuestionTo(
                                    dragSlideId,
                                    s.id,
                                    gk,
                                    "before",
                                  );
                                  setDragSlideId(null);
                                }}
                              >
                                {/* Page number = slide position in the lesson */}
                                <span
                                  className="badge bg-secondary flex-shrink-0"
                                  style={{ minWidth: 30 }}
                                  title={`Page ${i + 1}`}
                                >
                                  {i + 1}
                                </span>

                                {/* Drag handle */}
                                <span
                                  title="Drag"
                                  draggable
                                  onDragStart={() => onDragStartSlide(s.id)}
                                  onDragEnd={onDragEndSlide}
                                  style={{
                                    cursor: "grab",
                                    opacity: 0.85,
                                    userSelect: "none",
                                    display: "inline-flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <FaGripVertical />
                                </span>

                                {/* Title (click to jump) */}
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-start flex-grow-1"
                                  style={{
                                    textDecoration: "none",
                                    color: "inherit",
                                    fontWeight: isActive ? 700 : 600,
                                  }}
                                  onClick={() => jumpToSlide(i)}
                                  title={s.id}
                                >
                                  <div style={{ lineHeight: 1.2 }}>{label}</div>
                                  <div className="text-muted small">
                                    {s.kind}
                                  </div>
                                </button>

                                {/* Edit (jump) */}
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => jumpToSlide(i)}
                                  title="Open"
                                >
                                  <FaEdit />
                                </Button>

                                {/* Delete */}
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => deleteSlideById(s.id)}
                                  title="Delete"
                                >
                                  <FaTrashAlt />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          ) : (
            <div className="d-flex flex-column gap-2">
              {outlineQuestions.length === 0 ? (
                <div className="text-muted small">No items found.</div>
              ) : (
                outlineQuestions.map(({ s, i }) => {
                  const isActive = draft.slides[idx]?.id === s.id;
                  const label = (s.title || "").trim() || "(Untitled)";

                  return (
                    <div
                      key={s.id}
                      className={`d-flex align-items-center gap-2 px-2 py-2 rounded ${
                        isActive ? "bg-light" : ""
                      }`}
                      style={{ border: "1px solid rgba(0,0,0,0.08)" }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (!dragSlideId || dragSlideId === s.id) return;
                        // ✅ flat reorder: drop onto slide => insert before it (no grouping)
                        moveQuestionTo(dragSlideId, s.id, UNGROUPED, "before");
                        setDragSlideId(null);
                      }}
                    >
                      {/* Page number = slide position in the lesson */}
                      <span
                        className="badge bg-secondary flex-shrink-0"
                        style={{ minWidth: 30 }}
                        title={`Page ${i + 1}`}
                      >
                        {i + 1}
                      </span>

                      <span
                        title="Drag"
                        draggable
                        onDragStart={() => onDragStartSlide(s.id)}
                        onDragEnd={onDragEndSlide}
                        style={{
                          cursor: "grab",
                          opacity: 0.85,
                          userSelect: "none",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        <FaGripVertical />
                      </span>

                      <button
                        type="button"
                        className="btn btn-link p-0 text-start flex-grow-1"
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                          fontWeight: isActive ? 700 : 600,
                        }}
                        onClick={() => jumpToSlide(i)}
                        title={s.id}
                      >
                        <div style={{ lineHeight: 1.2 }}>{label}</div>
                        <div className="text-muted small">{s.kind}</div>
                      </button>

                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => jumpToSlide(i)}
                        title="Open"
                      >
                        <FaEdit />
                      </Button>

                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => deleteSlideById(s.id)}
                        title="Delete"
                      >
                        <FaTrashAlt />
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          )}

          <div className="text-muted small mt-3">
            Tip: Drag an item onto another to reorder, or drag into a group box
            to assign it. The number is the page (slide position in the lesson).
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* <Modal show={metaOpen} onHide={() => setMetaOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Lesson Information</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Call Sign</Form.Label>
            <Form.Control
              value={
                typeof draft.runtime_params?.call_sign === "string"
                  ? draft.runtime_params.call_sign
                  : ""
              }
              onChange={(e) =>
                patch((d) => {
                  d.runtime_params.call_sign = e.target.value;
                })
              }
              placeholder="e.g. KLM123"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>ATIS (1 letter)</Form.Label>
            <Form.Control
              maxLength={1}
              value={
                typeof draft.runtime_params?.atis === "string"
                  ? draft.runtime_params.atis
                  : ""
              }
              onChange={(e) =>
                patch((d) => {
                  d.runtime_params.atis = e.target.value.toUpperCase();
                })
              }
              placeholder="A"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Altitude</Form.Label>
            <Form.Control
              value={
                typeof draft.runtime_params?.altitude === "string"
                  ? draft.runtime_params.altitude
                  : ""
              }
              onChange={(e) =>
                patch((d) => {
                  d.runtime_params.altitude = e.target.value;
                })
              }
              placeholder="2000 ft / FL180"
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="primary"
            disabled={!hasMeta}
            onClick={() => {
              setMetaOpen(false);
              toast.success("Lesson information added to draft");
            }}
          >
            Save
          </Button>

          <Button variant="secondary" onClick={() => setMetaOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal> */}
    </div>
  );
};

export default AddEditQuestion;
